import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PageComparisonService, PageComparisonResult, AnalysisStatusResponse, IncomingLink, IncomingLinksResponse, DomainTopic, TopicTier, TopicShareCoverageResponse, TopicShareCoverageDomain } from '../../services/page-comparison';
import { CrawlService, CrawlStatus, CrawlEstimate, DomainProcessingStatus, ProcessingStatus } from '../../services/crawl';
import { combineLatest, Subscription, timer, of } from 'rxjs';
import { switchMap, takeWhile, catchError, take } from 'rxjs/operators';
import { parseFullComparePrefillFromQuery } from './page-comparison-prefill';
import { environment } from '../../../environments/environment';
import { FeatureFlagService } from '../../services/feature-flag';
import { surfacedStoredIssues } from '../../utils/url-detail-onpage-issues';

type PageStatus = 'idle' | 'checking' | 'fetching' | 'analyzing' | 'completed' | 'error';

interface PageState {
  status: PageStatus;
  analysis: any | null;
  metadata: {
    dataSource: 'database' | 'api' | null;
    lastUpdated: string | null;
    taskId: string | null;
  };
  error: string | null;
  progress: {
    pagesCrawled: number;
    pagesInQueue: number;
  } | null;
}

interface DomainCrawlState {
  status: CrawlStatus | null;
  loading: boolean;
  starting: boolean;
  error: string | null;
}

interface DomainProcessingState {
  status: DomainProcessingStatus | null;
  loading: boolean;
  error: string | null;
}

interface SerpResult {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpResponse {
  keyword: string;
  results: SerpResult[];
}

interface SerpMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  keyword: string;
  locationCode: number;
  languageName: string;
}

interface Entity {
  keyword: string;
  normalizedKeyword: string;
  searchVolume: number;
  keywordDifficulty: number;
  cpc?: number;
  coreKeyword: string | null;
  sources: ('related' | 'suggestions' | 'ranked_keywords' | 'ranked_keywords_filtered')[];
  rankedFromPages: string[] | Array<{
    url: string;
    title: string;
    domain: string;
    position: number;
  }>;
  similarityScore: number;
}

interface SharedEntity extends Entity {
  domainCount: number;
  domains: string[];
}

interface EntityCostBreakdown {
  relatedKeywords?: number;
  suggestions?: number;
  rankedKeywords?: number;
  rankedKeywordsFiltered?: number;
  total: number;
}

interface EntityStats {
  totalEntities: number;
  fromRelated?: number;
  fromSuggestions?: number;
  fromRankedKeywords?: number;
  fromRankedKeywordsFiltered?: number;
  uniqueEntities: number;
  domainsAnalyzed?: number;
}

interface FilterDetails {
  inputTopic: string;
  discoveredCoreKeyword: string;
  additionalCoreKeywords: Array<{
    coreKeyword: string;
    bestEntity: string;
    similarityScore: number;
    entityCount: number;
  }>;
}

interface SharedEntityStats {
  totalDiscovered: number;
  afterDeduplication: number;
  afterSharedFilter: number;
  fromInitialDiscovery: number;
  fromFilteredDiscovery: number;
}

interface SharedEntityCostBreakdown {
  initialDiscovery: number;
  filteredDiscovery: number;
  total: number;
}

interface SharedEntityMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  domains: string[];
  locationCode: number;
  languageName: string;
}

interface RelatedPage {
  url: string;
  title?: string;
  sharedKeywords: string[];
  keywordCount: number;
  totalSearchVolume: number;
  avgPosition: number;
  relevanceScore: number;
}

interface RelatedPagesData {
  domain: string;
  originalUrl: string;
  relatedPages: RelatedPage[];
  metadata: {
    totalPagesFound: number;
    pagesReturned: number;
    apiCallsMade: number;
    hasMoreResults: boolean;
    costBreakdown: {
      rankedKeywords: number;
      total: number;
    };
  };
}

interface KeywordOverlapAnalysis {
  sharedKeywords: Array<{
    keyword: string;
    yourPosition: number;
    competitorPosition: number;
    searchVolume?: number;
  }>;
  yourUniqueKeywords: Array<{ keyword: string; position: number; searchVolume?: number }>;
  competitorUniqueKeywords: Array<{ keyword: string; position: number; searchVolume?: number }>;
  // Summary stats
  sharedCount: number;
  yourUniqueCount: number;
  competitorUniqueCount: number;
  sharedAvgPositionYour: number;
  sharedAvgPositionCompetitor: number;
}

interface ContentOpportunity {
  keyword: string;
  position: number;
  searchVolume: number;
}

/**
 * TopicFitData represents the domain's authority on a page's main topic.
 * Used to show how well each domain covers the page's primary topic.
 */
interface TopicFitData {
  /** The page's main topic (highest weight from topicSignals) */
  mainTopicSlug: string;
  mainTopicLabel: string;
  /** Whether domain has topic authority data */
  hasData: boolean;
  /** Whether the main topic was found in domain's topic authority */
  topicFound: boolean;
  /** The topic tier (owned, aspirational, etc.) - null if not found */
  tier: TopicTier | null;
  /** Authority score (0-100) - null if not found */
  authorityScore: number | null;
  /** Loading state */
  loading: boolean;
  /** Error message if failed to load */
  error: string | null;
}

/**
 * TopicShareCoverageData represents the shared entity coverage from Topic Share 3.
 * Shows how many shared topic entities each domain covers.
 */
interface TopicShareCoverageData {
  /** Whether Topic Share 3 data is available for this topic + domains */
  available: boolean;
  /** The topic being analyzed */
  topic: string;
  /** Coverage data for your domain */
  yourDomain: {
    domain: string;
    entitiesCovered: number;
    totalEntities: number;
    coveragePercentage: number;
    topUrls: Array<{ url: string; title: string; entitiesCount: number }>;
  } | null;
  /** Coverage data for competitor domain */
  competitorDomain: {
    domain: string;
    entitiesCovered: number;
    totalEntities: number;
    coveragePercentage: number;
    topUrls: Array<{ url: string; title: string; entitiesCount: number }>;
  } | null;
  /** Total shared entities in the Topic Share analysis */
  sharedEntitiesCount: number;
  /** When the Topic Share data was last updated */
  lastUpdated: Date | null;
  /** Reason if data is not available */
  reason: string | null;
  /** Loading state */
  loading: boolean;
  /** Error message if failed to load */
  error: string | null;
}

/**
 * DiagnosisBullet represents a single insight in the "Why They Probably Win" banner.
 * 
 * This is part of a LIVING FEATURE that should evolve as new data becomes available.
 * For example, contextual links were recently added, and the diagnosis rules now include
 * link advantage analysis. Future enhancements might include:
 * - Domain Topic Authority comparison
 * - Backlink profile analysis
 * - SERP feature coverage
 * - Entity coverage gaps
 * 
 * See docs/features/PAGE_COMPARISON.md for full documentation.
 */
interface DiagnosisBullet {
  /** Human-readable explanation text */
  text: string;
  /** Category for grouping/styling */
  category: 'keywords' | 'links' | 'freshness' | 'content' | 'topicAuthority';
  /** Priority for sorting (1 = highest priority, shown first) */
  priority: number;
  /** Who has the advantage */
  winner: 'you' | 'competitor' | 'tie';
}

@Component({
  selector: 'app-page-comparison',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, RouterModule],
  templateUrl: './page-comparison.html',
  styleUrl: './page-comparison.css'
})
export class PageComparisonComponent implements OnInit, OnDestroy {
  yourPageUrl: string = '';
  competitorPageUrl: string = '';
  
  yourPage: PageState = {
    status: 'idle',
    analysis: null,
    metadata: {
      dataSource: null,
      lastUpdated: null,
      taskId: null
    },
    error: null,
    progress: null
  };

  competitorPage: PageState = {
    status: 'idle',
    analysis: null,
    metadata: {
      dataSource: null,
      lastUpdated: null,
      taskId: null
    },
    error: null,
    progress: null
  };

  loading = false;
  subscriptions = new Subscription();

  // Dropdown states
  yourPageTopicSignalsExpanded = false;
  yourPageCriticalIssuesExpanded = false;
  yourPageRankedKeywordsExpanded = false;
  competitorPageTopicSignalsExpanded = false;
  competitorPageCriticalIssuesExpanded = false;
  competitorPageRankedKeywordsExpanded = false;
  relatedPagesExpanded = false;
  yourPageInternalLinksExpanded = false;
  competitorPageInternalLinksExpanded = false;
  keywordOpportunitiesExpanded = false;

  // Entity discovery state
  selectedTopicSignals: Array<{ topicSlug: string; label: string; weight: number; source: 'shared' | 'yourPage' | 'competitorPage' }> = [];
  
  // SERP results
  serpResults: SerpResponse | null = null;
  serpMetadata: SerpMetadata | null = null;
  loadingSerp = false;
  serpCost = 0;
  
  // Initial entities
  initialEntities: Entity[] = [];
  loadingInitialEntities = false;
  initialEntityCostBreakdown: EntityCostBreakdown | null = null;
  initialEntityStats: EntityStats | null = null;
  initialEntityMetadata: {
    dataSource: 'database' | 'api' | 'unknown';
    lastUpdated: string | null;
    topic: string;
    coreKeyword: string;
    locationCode: number;
    languageName: string;
    pagesAnalyzed?: number;
  } | null = null;
  showAllInitialEntities = false;
  initialEntitiesDisplayLimit = 50;
  
  // Filtering
  filteredCoreKeywords: string[] = [];
  filterDetails: FilterDetails | null = null;
  loadingFilter = false;
  
  // Filtered entities
  filteredEntities: Entity[] = [];
  loadingFilteredEntities = false;
  filteredEntityCostBreakdown: EntityCostBreakdown | null = null;
  filteredEntityStats: EntityStats | null = null;
  showAllFilteredEntities = false;
  filteredEntitiesDisplayLimit = 50;
  domainTotalCounts: Record<string, number> = {};
  
  // Shared entities
  sharedEntities: SharedEntity[] = [];
  loadingSharedEntities = false;
  sharedEntityStats: SharedEntityStats | null = null;
  sharedEntityCostBreakdown: SharedEntityCostBreakdown | null = null;
  sharedEntityMetadata: SharedEntityMetadata | null = null;
  showAllSharedEntities = false;
  sharedEntitiesDisplayLimit = 50;
  
  // Related pages
  relatedPagesData: {
    yourPage: RelatedPagesData | null;
    competitorPage: RelatedPagesData | null;
  } | null = null;
  loadingRelatedPages = false;
  relatedPagesError: string | null = null;
  relatedPagesMetadata: {
    mode: string;
    dataSource: 'api' | 'database' | 'unknown';
    sharedEntityKeywordCount: number;
    locationCode: number;
    languageName: string;
    filters: {
      type: string;
      field: string;
      totalKeywords: number;
      chunkSize: number;
      description: string;
    };
    apisUsed: Array<{
      api: string;
      reason: string;
      endpoint: string;
    }>;
    perDomainCost: Record<string, number>;
    totalCost: number;
  } | null = null;
  relatedPagesCostTotal: number = 0;
  relatedPagesHistoricalCostTotal: number = 0;

  // Progress tracking for user mode
  comparisonProgress: {
    currentStep: string;
    completedSteps: string[];
    estimatedTimeRemaining: number; // seconds
    stepDetails: Record<string, { status: 'pending' | 'in-progress' | 'completed' | 'error', cost?: number }>;
  } | null = null;
  isViewingPersistedComparison = false;
  executionId: string | null = null;
  
  // Total cost
  totalEntityDiscoveryRunCost = 0;
  totalEntityDiscoveryHistoricalCost = 0;
  initialEntityRunCost = 0;
  initialEntityHistoricalCost = 0;
  filteredEntityRunCost = 0;
  filteredEntityHistoricalCost = 0;
  sharedEntityHistoricalCost = 0;

  // Internal links display settings
  readonly internalLinksDisplayLimit = 25;
  showAllInternalLinks: { yourPage: boolean; competitorPage: boolean } = {
    yourPage: false,
    competitorPage: false
  };

  // Incoming links state
  incomingLinks: { yourPage: IncomingLink[] | null; competitorPage: IncomingLink[] | null } = {
    yourPage: null,
    competitorPage: null
  };
  incomingLinksLoading: { yourPage: boolean; competitorPage: boolean } = {
    yourPage: false,
    competitorPage: false
  };
  incomingLinksExpanded: { yourPage: boolean; competitorPage: boolean } = {
    yourPage: false,
    competitorPage: false
  };
  showAllIncomingLinks: { yourPage: boolean; competitorPage: boolean } = {
    yourPage: false,
    competitorPage: false
  };
  incomingLinksError: { yourPage: string | null; competitorPage: string | null } = {
    yourPage: null,
    competitorPage: null
  };
  incomingLinksMetadata: { yourPage: any | null; competitorPage: any | null } = {
    yourPage: null,
    competitorPage: null
  };

  // Domain Topic Authority state (for Topic Fit snippet)
  domainTopics: Map<string, DomainTopic[]> = new Map();
  domainTopicsLoading: Map<string, boolean> = new Map();
  domainTopicsError: Map<string, string | null> = new Map();

  // Topic Share Coverage state (for mini Topic Share hook)
  // Cache key format: "topic:domain1:domain2" (domains sorted alphabetically)
  topicShareCoverageData: Map<string, TopicShareCoverageData> = new Map();
  topicShareCoverageLoading: Map<string, boolean> = new Map();
  topicShareCoverageError: Map<string, string | null> = new Map();
  topicShareUrlsExpanded: boolean = false;
  
  // Defaults
  readonly defaultLocationCode: number = 2840;
  readonly defaultLanguageName: string = 'English';

  // Crawl management state
  domainCrawlStates: Map<string, DomainCrawlState> = new Map();
  domainProcessingStates: Map<string, DomainProcessingState> = new Map();
  crawlPollingIntervals: Map<string, any> = new Map();
  showCrawlPrompt: boolean = false;

  // Processing step triggers
  processingRunning: Map<string, boolean> = new Map();
  stepRunning: Map<string, boolean> = new Map();

  // Crawl estimate state
  crawlEstimates: Map<string, CrawlEstimate> = new Map();
  crawlEstimateLoading: Map<string, boolean> = new Map();
  crawlEstimateError: Map<string, string | null> = new Map();
  crawlConfirmed: Map<string, boolean> = new Map();
  enableCoreWebVitals: Map<string, boolean> = new Map();

  // Share link
  shareLoading = false;
  shareUrl: string | null = null;
  shareCopied = false;
  shareError: string | null = null;

  // Diagnosis banner cache (memoization to avoid recomputation on every template check)
  private _diagnosisCache: DiagnosisBullet[] | null = null;
  private _diagnosisCacheKey: string = '';

  constructor(
    private pageComparisonService: PageComparisonService,
    private crawlService: CrawlService,
    private http: HttpClient,
    private route: ActivatedRoute,
    public featureFlags: FeatureFlagService
  ) {}

  ngOnInit() {
    const routeSub = this.route.paramMap.subscribe(params => {
      const executionId = params.get('executionId');
      if (!executionId) {
        this.isViewingPersistedComparison = false;
        return;
      }
      this.loadPersistedComparison(executionId);
    });
    this.subscriptions.add(routeSub);

    const prefillSub = combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(take(1))
      .subscribe(([params, query]) => {
        const executionId = params.get('executionId');
        const prefill = parseFullComparePrefillFromQuery(query, executionId);
        if (!prefill) return;
        this.yourPageUrl = prefill.yourPageUrl;
        this.competitorPageUrl = prefill.competitorPageUrl;
      });
    this.subscriptions.add(prefillSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.crawlPollingIntervals.forEach((interval) => clearInterval(interval));
    this.crawlPollingIntervals.clear();
  }

  comparePages() {
    if (!this.yourPageUrl || !this.competitorPageUrl) {
      alert('Please enter both URLs');
      return;
    }

    // Reset states
    this.yourPage = { ...this.yourPage, status: 'checking', error: null };
    this.competitorPage = { ...this.competitorPage, status: 'checking', error: null };
    this.loading = true;

    // Start comparison
    const sub = this.pageComparisonService.comparePages(this.yourPageUrl, this.competitorPageUrl)
      .subscribe({
        next: (response) => {
          this.loading = false;
          
          // Process your page
          this.processPageResult(response.data.yourPage, 'yourPage');
          
          // Process competitor page
          this.processPageResult(response.data.competitorPage, 'competitorPage');
        },
        error: (error) => {
          this.loading = false;
          console.error('Comparison error:', error);
          this.yourPage.status = 'error';
          this.yourPage.error = error.message || 'Failed to compare pages';
          this.competitorPage.status = 'error';
          this.competitorPage.error = error.message || 'Failed to compare pages';
        }
      });

    this.subscriptions.add(sub);
  }

  private loadPersistedComparison(executionId: string): void {
    this.loading = true;
    this.isViewingPersistedComparison = true;
    this.executionId = executionId;
    this.resetEntityDiscoveryState();
    this.relatedPagesData = null;
    this.relatedPagesError = null;
    this.showCrawlPrompt = false;

    const sub = this.pageComparisonService.getComparisonResource(executionId).subscribe({
      next: (response) => {
        this.loading = false;
        const payload = response.data;
        this.yourPageUrl = payload.execution.yourPageUrl || '';
        this.competitorPageUrl = payload.execution.competitorPageUrl || '';

        this.yourPage = {
          ...this.yourPage,
          status: payload.yourPage.analysis ? 'completed' : 'error',
          analysis: payload.yourPage.analysis || null,
          metadata: {
            dataSource: payload.yourPage.analysis ? 'database' : null,
            lastUpdated: payload.yourPage.metadata?.lastUpdated || null,
            taskId: null
          },
          error: payload.yourPage.analysis ? null : 'No saved analysis found for this URL.',
          progress: null
        };

        this.competitorPage = {
          ...this.competitorPage,
          status: payload.competitorPage.analysis ? 'completed' : 'error',
          analysis: payload.competitorPage.analysis || null,
          metadata: {
            dataSource: payload.competitorPage.analysis ? 'database' : null,
            lastUpdated: payload.competitorPage.metadata?.lastUpdated || null,
            taskId: null
          },
          error: payload.competitorPage.analysis ? null : 'No saved analysis found for this URL.',
          progress: null
        };

        const stepStatusByName = new Map<string, 'pending' | 'in-progress' | 'completed' | 'error'>(
          (payload.execution.steps || []).map((step: any) => [step.step, step.status])
        );
        const hasBothAnalyses = !!(this.yourPage.analysis && this.competitorPage.analysis);
        const assumeFullRunCompleted = hasBothAnalyses && !payload.execution.error;
        const fallbackStepStatus: 'pending' | 'completed' = assumeFullRunCompleted ? 'completed' : 'pending';
        const analyzePagesStatus: 'pending' | 'in-progress' | 'completed' | 'error' =
          hasBothAnalyses ? 'completed' : 'error';
        const resolvedStepStatus = (stepName: string): 'pending' | 'in-progress' | 'completed' | 'error' =>
          stepStatusByName.get(stepName) || fallbackStepStatus;

        const completedSteps = [
          ...(analyzePagesStatus === 'completed' ? ['analyze-pages'] : []),
          ...['serp', 'initial-entities', 'filter', 'filtered-entities', 'shared-entities', 'related-pages']
            .filter(step => resolvedStepStatus(step) === 'completed')
        ];

        this.comparisonProgress = {
          currentStep: 'Completed!',
          completedSteps,
          estimatedTimeRemaining: 0,
          stepDetails: {
            'analyze-pages': { status: analyzePagesStatus },
            'serp': { status: resolvedStepStatus('serp') },
            'initial-entities': { status: resolvedStepStatus('initial-entities') },
            'filter': { status: resolvedStepStatus('filter') },
            'filtered-entities': { status: resolvedStepStatus('filtered-entities') },
            'shared-entities': { status: resolvedStepStatus('shared-entities') },
            'related-pages': { status: resolvedStepStatus('related-pages') }
          }
        };

        // Keep persisted comparison behavior aligned with fresh runs:
        // link/classification warnings depend on domain processing status.
        this.refreshDomainStatuses();

        if (this.hasAnalysis(this.yourPage)) {
          this.loadIncomingLinksSummary('yourPage').catch(() => {});
          if (this.yourPageDomain) {
            this.loadDomainTopics(this.yourPageDomain).catch(() => {});
          }
        }
        if (this.hasAnalysis(this.competitorPage)) {
          this.loadIncomingLinksSummary('competitorPage').catch(() => {});
          if (this.competitorPageDomain) {
            this.loadDomainTopics(this.competitorPageDomain).catch(() => {});
          }
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Failed to load persisted comparison:', error);
        this.yourPage.status = 'error';
        this.competitorPage.status = 'error';
        this.yourPage.error = error.error?.message || 'Failed to load comparison.';
        this.competitorPage.error = error.error?.message || 'Failed to load comparison.';
      }
    });

    this.subscriptions.add(sub);
  }

  private processPageResult(result: PageComparisonResult, pageKey: 'yourPage' | 'competitorPage') {
    const page = pageKey === 'yourPage' ? this.yourPage : this.competitorPage;

    if (result.status === 'completed' && result.analysis) {
      // Analysis already exists
      page.status = 'completed';
      page.analysis = result.analysis;
      page.metadata = {
        dataSource: result.metadata.dataSource,
        lastUpdated: result.metadata.lastUpdated || null,
        taskId: null
      };
      // Load domain topics for Topic Fit snippet (optional, fails gracefully)
      const domain = pageKey === 'yourPage' ? this.yourPageDomain : this.competitorPageDomain;
      if (domain) {
        this.loadDomainTopics(domain).catch(err => {
          console.error(`Failed to load domain topics for ${domain}:`, err);
          // Don't show error to user - Topic Fit will show "not available"
        });
      }
      this.loadIncomingLinksSummary(pageKey).catch(() => {});
    } else if (result.status === 'pending' && result.taskId) {
      // Need to poll for completion
      page.status = 'analyzing';
      page.metadata = {
        dataSource: result.metadata.dataSource,
        lastUpdated: null,
        taskId: result.taskId
      };
      
      // Start polling
      this.startPolling(result.taskId, pageKey);
    } else {
      page.status = 'error';
      page.error = 'Unexpected response format';
    }
  }

  private startPolling(taskId: string, pageKey: 'yourPage' | 'competitorPage') {
    const page = pageKey === 'yourPage' ? this.yourPage : this.competitorPage;
    
    // Poll immediately, then every 5 seconds
    const pollSub = timer(0, 5000).pipe(
      switchMap(() => this.pageComparisonService.getAnalysisStatus(taskId)),
      takeWhile((response) => {
        const status = response.data?.status;
        if (status === 'pending') {
          // Update progress
          if (response.data.progress) {
            page.progress = {
              pagesCrawled: response.data.progress.pagesCrawled || 0,
              pagesInQueue: response.data.progress.pagesInQueue || 0
            };
          }
          return true; // Continue polling
        }
        return false; // Stop polling
      }, true), // inclusive - emit last value
      catchError((error) => {
        console.error('Polling error:', error);
        page.status = 'error';
        page.error = error.message || 'Failed to check analysis status';
        return of(null);
      })
    ).subscribe({
      next: (response) => {
        if (!response) return;

        const status = response.data?.status;
        
        if (status === 'completed' && response.data.analysis) {
          page.status = 'completed';
          page.analysis = response.data.analysis;
          page.metadata = {
            dataSource: response.data.metadata?.dataSource || 'api',
            lastUpdated: response.data.metadata?.lastUpdated || null,
            taskId: null
          };
          page.progress = null;
          
          // Load domain topics for Topic Fit snippet (optional, fails gracefully)
          const domain = pageKey === 'yourPage' ? this.yourPageDomain : this.competitorPageDomain;
          if (domain) {
            this.loadDomainTopics(domain).catch(err => {
              console.error(`Failed to load domain topics for ${domain}:`, err);
              // Don't show error to user - Topic Fit will show "not available"
            });
          }
          this.loadIncomingLinksSummary(pageKey).catch(() => {});
        } else if (status === 'failed') {
          page.status = 'error';
          page.error = response.data.error || 'Analysis failed';
          page.progress = null;
        }
      }
    });

    this.subscriptions.add(pollSub);
  }

  getStatusMessage(page: PageState): string {
    switch (page.status) {
      case 'idle':
        return 'Ready';
      case 'checking':
        return 'Checking database...';
      case 'fetching':
        return 'Creating analysis task...';
      case 'analyzing':
        if (page.progress) {
          return `Analyzing page... (${page.progress.pagesCrawled} pages crawled, ${page.progress.pagesInQueue} in queue)`;
        }
        return 'Analyzing page...';
      case 'completed':
        return 'Analysis complete';
      case 'error':
        return `Error: ${page.error || 'Unknown error'}`;
      default:
        return 'Unknown status';
    }
  }

  hasAnalysis(page: PageState): boolean {
    return page.status === 'completed' && page.analysis !== null;
  }

  getPageTitle(page: PageState): string {
    if (!page.analysis) return 'N/A';
    return page.analysis.rawData?.pageData?.meta?.title || 
           page.analysis.rawData?.summary?.url || 
           'Unknown';
  }

  getOnPageScore(page: PageState): number | null {
    return page.analysis?.onpageScore || null;
  }

  getWordCount(page: PageState): number | null {
    return page.analysis?.wordCount || null;
  }

  getTopicSignalsCount(page: PageState): number {
    return page.analysis?.topic?.topicSignals?.length || 0;
  }

  getLinksCount(page: PageState): number {
    return page.analysis?.links?.internalLinks?.length || 0;
  }

  getCriticalIssuesCount(page: PageState): number {
    return surfacedStoredIssues(page.analysis?.criticalIssues).length;
  }

  getCostBreakdown(page: PageState): any | null {
    return page.analysis?.costBreakdown || null;
  }

  getTotalCost(page: PageState): number {
    const pageCost = page.analysis?.costBreakdown?.total || 0;
    const pageRunCost = page.metadata.dataSource === 'api' ? pageCost : 0;
    // Shared discovery is split equally between both pages for display.
    return pageRunCost + (this.totalEntityDiscoveryRunCost / 2);
  }

  getHistoricalCost(page: PageState): number {
    const pageCost = page.analysis?.costBreakdown?.total || 0;
    const pageHistoricalCost = page.metadata.dataSource === 'database' ? pageCost : 0;
    // Shared discovery is split equally between both pages for display.
    return pageHistoricalCost + (this.totalEntityDiscoveryHistoricalCost / 2);
  }

  formatCost(cost: number): string {
    if (cost === 0) return '$0.00';
    return `$${cost.toFixed(4)}`;
  }

  // Link Classification Methods
  getInternalLinks(page: PageState): any[] {
    return page.analysis?.links?.internalLinks || [];
  }

  getContextualLinks(page: PageState): any[] {
    const links = this.getInternalLinks(page);
    return links.filter(link => link.linkType === 'contextual');
  }

  getStructuralLinks(page: PageState): any[] {
    const links = this.getInternalLinks(page);
    return links.filter(link => link.linkType === 'structural');
  }

  getSamePageLinks(page: PageState): any[] {
    const links = this.getInternalLinks(page);
    return links.filter(link => link.linkType === 'same_page');
  }

  getIdentifiedLinks(page: PageState): any[] {
    const links = this.getInternalLinks(page);
    return links.filter(link => 
      link.linkType === 'contextual' || 
      link.linkType === 'structural' || 
      link.linkType === 'same_page'
    );
  }

  getLinkStats(page: PageState): { total: number; contextual: number; structural: number; samePage: number; unknown: number } {
    const links = this.getInternalLinks(page);
    return {
      total: links.length,
      contextual: links.filter(l => l.linkType === 'contextual').length,
      structural: links.filter(l => l.linkType === 'structural').length,
      samePage: links.filter(l => l.linkType === 'same_page').length,
      unknown: links.filter(l => !l.linkType || l.linkType === 'unknown').length
    };
  }

  hasUnclassifiedLinks(page: PageState): boolean {
    const stats = this.getLinkStats(page);
    return stats.unknown > 0;
  }

  toggleInternalLinks(pageKey: 'yourPage' | 'competitorPage'): void {
    if (pageKey === 'yourPage') {
      this.yourPageInternalLinksExpanded = !this.yourPageInternalLinksExpanded;
    } else {
      this.competitorPageInternalLinksExpanded = !this.competitorPageInternalLinksExpanded;
    }
  }

  isInternalLinksExpanded(pageKey: 'yourPage' | 'competitorPage'): boolean {
    return pageKey === 'yourPage' ? this.yourPageInternalLinksExpanded : this.competitorPageInternalLinksExpanded;
  }

  toggleShowAllInternalLinks(pageKey: 'yourPage' | 'competitorPage'): void {
    this.showAllInternalLinks[pageKey] = !this.showAllInternalLinks[pageKey];
  }

  getFilteredInternalLinks(page: PageState): any[] {
    return this.getInternalLinks(page).filter(link => link.linkType === 'contextual' || link.linkType === 'same_page');
  }

  getDisplayedInternalLinks(page: PageState, pageKey: 'yourPage' | 'competitorPage'): any[] {
    const links = this.getFilteredInternalLinks(page);
    if (this.showAllInternalLinks[pageKey]) {
      return links;
    }
    return links.slice(0, this.internalLinksDisplayLimit);
  }

  hasMoreInternalLinks(page: PageState, pageKey: 'yourPage' | 'competitorPage'): boolean {
    const links = this.getFilteredInternalLinks(page);
    return !this.showAllInternalLinks[pageKey] && links.length > this.internalLinksDisplayLimit;
  }

  getLinkTypeLabel(linkType: string | undefined): string {
    switch (linkType) {
      case 'contextual':
        return 'Contextual';
      case 'structural':
        return 'Structural';
      case 'same_page':
        return 'Same-page';
      case 'unknown':
      default:
        return 'Unknown';
    }
  }

  getLinkTypeTooltip(linkType: string | undefined): string {
    switch (linkType) {
      case 'contextual':
        return 'In-content link that appears on a small subset of pages.';
      case 'structural':
        return 'Navigation/footer/sidebar link that appears across many pages.';
      case 'same_page':
        return 'Anchor link within the same page (table of contents or section jump).';
      case 'unknown':
      default:
        return 'Not enough signals to classify this link yet.';
    }
  }

  getLinkTypeClass(linkType: string | undefined): string {
    switch (linkType) {
      case 'contextual':
        return 'link-type-contextual';
      case 'structural':
        return 'link-type-structural';
      case 'same_page':
        return 'link-type-same-page';
      case 'unknown':
      default:
        return 'link-type-unknown';
    }
  }

  getLinkSourceUrl(page: PageState): string {
    return page.analysis?.rawData?.pageData?.url ||
      page.analysis?.rawData?.summary?.url ||
      'Current page';
  }

  formatAnchorText(anchorText: string | undefined): string {
    if (!anchorText || anchorText.trim().length === 0) {
      return '—';
    }
    return anchorText;
  }

  getLinkClassificationStatus(domain: string): ProcessingStatus | null {
    const state = this.getDomainProcessingState(domain);
    return state.status?.linksClassify.status || null;
  }

  shouldShowLinkWarning(domain: string, page: PageState): boolean {
    if (this.getLinksCount(page) === 0) return false;
    const processingState = this.getDomainProcessingState(domain);
    if (!processingState.status && !processingState.loading) {
      this.loadProcessingStatus(domain);
      return false;
    }
    if (processingState.loading || !processingState.status) return false;
    const status = this.getLinkClassificationStatus(domain);
    return status === 'no_data' || status === 'not_started';
  }

  shouldShowLinkBreakdown(domain: string, page: PageState): boolean {
    if (this.getLinksCount(page) === 0) return false;
    const processingState = this.getDomainProcessingState(domain);
    if (!processingState.status && !processingState.loading) {
      this.loadProcessingStatus(domain);
      return false;
    }
    if (processingState.loading || !processingState.status) return false;
    const status = this.getLinkClassificationStatus(domain);
    if (status === 'no_data' || status === 'not_started') return false;
    return this.getIdentifiedLinks(page).length > 0;
  }

  // Incoming Links Methods
  async loadIncomingLinks(pageKey: 'yourPage' | 'competitorPage'): Promise<void> {
    const page = pageKey === 'yourPage' ? this.yourPage : this.competitorPage;
    
    if (!page.analysis) {
      this.incomingLinksError[pageKey] = 'Page analysis not available';
      return;
    }

    // Get urlId - handle both string and ObjectId formats
    let urlId: string | null = null;
    if (page.analysis.urlId) {
      urlId = typeof page.analysis.urlId === 'string' 
        ? page.analysis.urlId 
        : (page.analysis.urlId as any)?._id?.toString() || (page.analysis.urlId as any)?.toString();
    }

    if (!urlId) {
      this.incomingLinksError[pageKey] = 'URL ID not available in analysis';
      return;
    }

    // Don't reload if already loaded (unless forced)
    if (this.incomingLinks[pageKey] !== null && !this.incomingLinksLoading[pageKey]) {
      return;
    }

    this.incomingLinksLoading[pageKey] = true;
    this.incomingLinksError[pageKey] = null;

    try {
      const response = await this.pageComparisonService.getIncomingLinks(urlId).toPromise();
      
      if (response?.success) {
        this.incomingLinks[pageKey] = response.data.incomingLinks;
        this.incomingLinksMetadata[pageKey] = response.data.metadata;
      } else {
        this.incomingLinksError[pageKey] = 'Failed to load incoming links';
        this.incomingLinks[pageKey] = [];
      }
    } catch (error: any) {
      console.error(`Error loading incoming links for ${pageKey}:`, error);
      this.incomingLinksError[pageKey] = error.error?.message || error.message || 'Failed to load incoming links';
      this.incomingLinks[pageKey] = [];
    } finally {
      this.incomingLinksLoading[pageKey] = false;
    }
  }

  async loadIncomingLinksSummary(pageKey: 'yourPage' | 'competitorPage'): Promise<void> {
    const page = pageKey === 'yourPage' ? this.yourPage : this.competitorPage;
    if (!page.analysis) return;
    if (this.incomingLinksMetadata[pageKey]) return;

    let urlId: string | null = null;
    if (page.analysis.urlId) {
      urlId = typeof page.analysis.urlId === 'string'
        ? page.analysis.urlId
        : (page.analysis.urlId as any)?._id?.toString() || (page.analysis.urlId as any)?.toString();
    }
    if (!urlId) return;

    try {
      const response = await this.pageComparisonService.getIncomingLinks(urlId, { summaryOnly: true }).toPromise();
      if (response?.success) {
        this.incomingLinksMetadata[pageKey] = response.data.metadata;
      }
    } catch {
      // Silent fail: detailed incoming links remain available on manual expand.
    }
  }

  getIncomingLinksCount(pageKey: 'yourPage' | 'competitorPage'): number {
    if (this.incomingLinks[pageKey] !== null) {
      return this.incomingLinks[pageKey]?.length || 0;
    }
    return this.incomingLinksMetadata[pageKey]?.totalIncomingLinks || 0;
  }

  getIncomingLinksStats(pageKey: 'yourPage' | 'competitorPage'): { 
    total: number; 
    contextual: number; 
    structural: number; 
    samePage: number; 
    unknown: number 
  } {
    if (this.incomingLinks[pageKey] === null && this.incomingLinksMetadata[pageKey]) {
      const meta = this.incomingLinksMetadata[pageKey]!;
      return {
        total: meta.totalIncomingLinks || 0,
        contextual: meta.contextualLinks || 0,
        structural: meta.structuralLinks || 0,
        samePage: meta.samePageLinks || 0,
        unknown: meta.unknownLinks || 0
      };
    }
    const links = this.incomingLinks[pageKey] || [];
    return {
      total: links.length,
      contextual: links.filter(l => l.linkType === 'contextual').length,
      structural: links.filter(l => l.linkType === 'structural').length,
      samePage: links.filter(l => l.linkType === 'same_page').length,
      unknown: links.filter(l => !l.linkType || l.linkType === 'unknown').length
    };
  }

  getDisplayedIncomingLinks(pageKey: 'yourPage' | 'competitorPage'): IncomingLink[] {
    const links = this.incomingLinks[pageKey] || [];
    // Filter to contextual and same-page links (similar to outgoing links)
    const filtered = links.filter(link => 
      link.linkType === 'contextual' || link.linkType === 'same_page'
    );
    
    if (this.showAllIncomingLinks[pageKey]) {
      return filtered;
    }
    return filtered.slice(0, this.internalLinksDisplayLimit);
  }

  toggleIncomingLinks(pageKey: 'yourPage' | 'competitorPage'): void {
    if (pageKey === 'yourPage') {
      this.incomingLinksExpanded.yourPage = !this.incomingLinksExpanded.yourPage;
      // Load on first expand if not loaded
      if (this.incomingLinksExpanded.yourPage && this.incomingLinks.yourPage === null) {
        this.loadIncomingLinks('yourPage');
      }
    } else {
      this.incomingLinksExpanded.competitorPage = !this.incomingLinksExpanded.competitorPage;
      // Load on first expand if not loaded
      if (this.incomingLinksExpanded.competitorPage && this.incomingLinks.competitorPage === null) {
        this.loadIncomingLinks('competitorPage');
      }
    }
  }

  isIncomingLinksExpanded(pageKey: 'yourPage' | 'competitorPage'): boolean {
    return pageKey === 'yourPage' ? this.incomingLinksExpanded.yourPage : this.incomingLinksExpanded.competitorPage;
  }

  toggleShowAllIncomingLinks(pageKey: 'yourPage' | 'competitorPage'): void {
    this.showAllIncomingLinks[pageKey] = !this.showAllIncomingLinks[pageKey];
  }

  hasMoreIncomingLinks(pageKey: 'yourPage' | 'competitorPage'): boolean {
    const links = this.incomingLinks[pageKey] || [];
    const filtered = links.filter(link => 
      link.linkType === 'contextual' || link.linkType === 'same_page'
    );
    return !this.showAllIncomingLinks[pageKey] && filtered.length > this.internalLinksDisplayLimit;
  }

  shouldShowIncomingLinksWarning(pageKey: 'yourPage' | 'competitorPage'): boolean {
    const metadata = this.incomingLinksMetadata[pageKey];
    if (!metadata) return false;
    return metadata.requiresDomainCrawl === true;
  }

  // ============================================================
  // TOPIC FIT (Domain Topic Authority) Methods
  // ============================================================
  // 
  // Shows how strong each domain is on the page's main topic using
  // Domain Topic Authority data. This is an OPTIONAL feature that
  // requires domain crawl + topic aggregation to be run first.
  //
  // See docs/features/PAGE_COMPARISON.md and docs/features/DOMAIN_TOPIC_AUTHORITY.md
  // ============================================================

  /**
   * Get the main topic from a page's topic signals.
   * Returns the highest-weight topic signal.
   * 
   * ## Topic Selection Logic (IMPORTANT - may need tuning)
   * 
   * This method determines the "main topic" of a page by selecting the topic signal
   * with the **highest weight**. The weight comes from the page analysis pipeline:
   * - LLM keyphrase extraction identifies topics
   * - Keyword density analysis assigns weights
   * 
   * This is used by:
   * - Topic Fit Card: Shows domain authority on this topic
   * - Competitive Analysis: Compares domains' topic authority
   * 
   * ## Why Topics Might Differ Between Pages
   * 
   * Two pages covering similar subjects may have different "main topics" because:
   * 1. Different content emphasis (one focuses on "tools", other on general "feedback")
   * 2. Different keyword targeting in the content
   * 3. Different parentTopic mappings from the topic aggregation
   * 
   * ## Future Tuning Options
   * 
   * - Use top N topics instead of just the highest weight
   * - Consider topic similarity/overlap between compared pages
   * - Allow manual topic selection for comparison
   * - Weight by relevance to the comparison context
   */
  getMainTopic(page: PageState): { topicSlug: string; label: string; weight: number } | null {
    const signals = this.getTopicSignals(page);
    if (signals.length === 0) {
      return null;
    }
    // Already sorted by weight descending from getTopicSignals()
    const topSignal = signals[0];
    return {
      topicSlug: topSignal.topicSlug || '',
      label: topSignal.label || topSignal.topicSlug || '',
      weight: topSignal.weight || 0
    };
  }

  /**
   * Load domain topics for a given domain.
   * Called when page analysis completes to enable Topic Fit display.
   */
  async loadDomainTopics(domain: string): Promise<void> {
    if (!domain) return;
    
    const normalizedDomain = domain.toLowerCase().trim();
    
    // Don't reload if already loaded or currently loading
    if (this.domainTopics.has(normalizedDomain) || this.domainTopicsLoading.get(normalizedDomain)) {
      return;
    }

    this.domainTopicsLoading.set(normalizedDomain, true);
    this.domainTopicsError.set(normalizedDomain, null);

    try {
      const response = await this.pageComparisonService.getDomainTopics(normalizedDomain).toPromise();
      
      if (response?.success && response.data?.parentTopics) {
        this.domainTopics.set(normalizedDomain, response.data.parentTopics);
      } else {
        // Domain exists but no topics aggregated yet
        this.domainTopics.set(normalizedDomain, []);
        if (response?.data?.message) {
          // Not an error, just info that aggregation hasn't been run
          this.domainTopicsError.set(normalizedDomain, null);
        }
      }
    } catch (error: any) {
      console.error(`Error loading domain topics for ${normalizedDomain}:`, error);
      // 404 means domain hasn't been crawled/aggregated - not a real error
      if (error.status === 404) {
        this.domainTopics.set(normalizedDomain, []);
        this.domainTopicsError.set(normalizedDomain, null);
      } else {
        this.domainTopicsError.set(normalizedDomain, error.error?.message || error.message || 'Failed to load domain topics');
      }
    } finally {
      this.domainTopicsLoading.set(normalizedDomain, false);
    }
  }

  /**
   * Get Topic Fit data for a page.
   * Returns authority tier and score for the page's main topic within its domain.
   */
  getTopicFitData(pageKey: 'yourPage' | 'competitorPage'): TopicFitData {
    const page = pageKey === 'yourPage' ? this.yourPage : this.competitorPage;
    const domain = pageKey === 'yourPage' ? this.yourPageDomain : this.competitorPageDomain;
    const normalizedDomain = domain.toLowerCase().trim();

    // Default empty state
    const emptyResult: TopicFitData = {
      mainTopicSlug: '',
      mainTopicLabel: '',
      hasData: false,
      topicFound: false,
      tier: null,
      authorityScore: null,
      loading: false,
      error: null
    };

    // Check if page has analysis
    if (!this.hasAnalysis(page)) {
      return emptyResult;
    }

    // Get main topic from page
    const mainTopic = this.getMainTopic(page);
    if (!mainTopic || !mainTopic.topicSlug) {
      return emptyResult;
    }

    // Check loading state
    const isLoading = this.domainTopicsLoading.get(normalizedDomain) || false;
    if (isLoading) {
      return {
        ...emptyResult,
        mainTopicSlug: mainTopic.topicSlug,
        mainTopicLabel: mainTopic.label,
        loading: true
      };
    }

    // Check for errors
    const error = this.domainTopicsError.get(normalizedDomain);
    if (error) {
      return {
        ...emptyResult,
        mainTopicSlug: mainTopic.topicSlug,
        mainTopicLabel: mainTopic.label,
        error
      };
    }

    // Check if domain topics are loaded
    const topics = this.domainTopics.get(normalizedDomain);
    if (!topics) {
      // Not loaded yet - trigger load
      this.loadDomainTopics(normalizedDomain);
      return {
        ...emptyResult,
        mainTopicSlug: mainTopic.topicSlug,
        mainTopicLabel: mainTopic.label,
        loading: true
      };
    }

    // Domain topics loaded but empty (no crawl/aggregation)
    if (topics.length === 0) {
      return {
        ...emptyResult,
        mainTopicSlug: mainTopic.topicSlug,
        mainTopicLabel: mainTopic.label,
        hasData: false
      };
    }

    // Search for matching topic in domain authority
    // Check both parentTopicSlug and childTopicSlugs for a match
    const normalizedMainTopic = mainTopic.topicSlug.toLowerCase().trim();
    const matchingTopic = topics.find(t => {
      // Direct match on parentTopicSlug
      if (t.parentTopicSlug.toLowerCase().trim() === normalizedMainTopic) {
        return true;
      }
      // Match via childTopicSlugs (the main topic might be a child that was merged)
      if (t.childTopicSlugs && t.childTopicSlugs.some(
        child => child.toLowerCase().trim() === normalizedMainTopic
      )) {
        return true;
      }
      return false;
    });

    if (!matchingTopic) {
      return {
        mainTopicSlug: mainTopic.topicSlug,
        mainTopicLabel: mainTopic.label,
        hasData: true,
        topicFound: false,
        tier: null,
        authorityScore: null,
        loading: false,
        error: null
      };
    }

    // Found matching topic
    return {
      mainTopicSlug: mainTopic.topicSlug,
      mainTopicLabel: matchingTopic.label || mainTopic.label,
      hasData: true,
      topicFound: true,
      tier: matchingTopic.topicTier || null,
      authorityScore: matchingTopic.authorityScore,
      loading: false,
      error: null
    };
  }

  /**
   * Format topic tier for display (capitalize first letter).
   */
  formatTopicTier(tier: TopicTier | null): string {
    if (!tier) return 'Unknown';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  }

  /**
   * Get CSS class for topic tier badge.
   */
  getTopicTierClass(tier: TopicTier | null): string {
    if (!tier) return 'tier-unknown';
    return `tier-${tier}`;
  }

  // ============================================
  // Topic Share Coverage Methods (Mini Hook)
  // ============================================

  /**
   * Generate cache key for Topic Share coverage.
   * Format: "topic:domain1:domain2" (domains sorted alphabetically)
   */
  private getTopicShareCacheKey(topic: string, domain1: string, domain2: string): string {
    const domains = [domain1.toLowerCase().trim(), domain2.toLowerCase().trim()].sort();
    return `${topic.toLowerCase().trim()}:${domains.join(':')}`;
  }

  /**
   * Check if we should show the Topic Share coverage card.
   * Returns true if both pages have analysis and at least one has topic signals.
   */
  shouldShowTopicShareCard(): boolean {
    return this.hasAnalysis(this.yourPage) && 
           this.hasAnalysis(this.competitorPage) &&
           (this.getTopicSignalsCount(this.yourPage) > 0 || this.getTopicSignalsCount(this.competitorPage) > 0);
  }

  /**
   * Load Topic Share coverage data from the backend.
   * This is a lightweight lookup that only reads cached Topic Share 3 data.
   */
  private loadTopicShareCoverage(topic: string, yourDomain: string, competitorDomain: string): void {
    const cacheKey = this.getTopicShareCacheKey(topic, yourDomain, competitorDomain);
    
    // Already loading
    if (this.topicShareCoverageLoading.get(cacheKey)) {
      return;
    }

    console.log(`📊 Loading Topic Share coverage for topic="${topic}", domains=[${yourDomain}, ${competitorDomain}]`);
    this.topicShareCoverageLoading.set(cacheKey, true);
    this.topicShareCoverageError.set(cacheKey, null);

    this.pageComparisonService.getTopicShareCoverage(topic, [yourDomain, competitorDomain])
      .subscribe({
        next: (response: TopicShareCoverageResponse) => {
          this.topicShareCoverageLoading.set(cacheKey, false);
          
          if (!response.success || !response.data) {
            this.topicShareCoverageError.set(cacheKey, 'Failed to load Topic Share coverage');
            return;
          }

          const data = response.data;
          
          if (!data.available) {
            // Data not available - store the reason
            this.topicShareCoverageData.set(cacheKey, {
              available: false,
              topic: topic,
              yourDomain: null,
              competitorDomain: null,
              sharedEntitiesCount: 0,
              lastUpdated: null,
              reason: data.reason || 'Topic Share analysis not yet run for this topic.',
              loading: false,
              error: null
            });
            return;
          }

          // Data available - extract coverage for each domain
          const yourDomainNorm = yourDomain.toLowerCase().trim();
          const competitorDomainNorm = competitorDomain.toLowerCase().trim();
          
          const yourCoverage = data.domains?.find(d => d.domain.toLowerCase().trim() === yourDomainNorm) || null;
          const competitorCoverage = data.domains?.find(d => d.domain.toLowerCase().trim() === competitorDomainNorm) || null;

          this.topicShareCoverageData.set(cacheKey, {
            available: true,
            topic: data.topic || topic,
            yourDomain: yourCoverage ? {
              domain: yourCoverage.domain,
              entitiesCovered: yourCoverage.entitiesCovered,
              totalEntities: yourCoverage.totalEntities,
              coveragePercentage: yourCoverage.coveragePercentage,
              topUrls: yourCoverage.topUrls || []
            } : null,
            competitorDomain: competitorCoverage ? {
              domain: competitorCoverage.domain,
              entitiesCovered: competitorCoverage.entitiesCovered,
              totalEntities: competitorCoverage.totalEntities,
              coveragePercentage: competitorCoverage.coveragePercentage,
              topUrls: competitorCoverage.topUrls || []
            } : null,
            sharedEntitiesCount: data.sharedEntitiesCount || 0,
            lastUpdated: data.lastUpdated ? new Date(data.lastUpdated) : null,
            reason: null,
            loading: false,
            error: null
          });
          
          console.log(`✅ Loaded Topic Share coverage: You ${yourCoverage?.coveragePercentage?.toFixed(1)}%, Competitor ${competitorCoverage?.coveragePercentage?.toFixed(1)}%`);
        },
        error: (error) => {
          console.error('Error loading Topic Share coverage:', error);
          this.topicShareCoverageLoading.set(cacheKey, false);
          this.topicShareCoverageError.set(cacheKey, 'Failed to load Topic Share coverage');
        }
      });
  }

  /**
   * Convert a topic slug to human-readable format.
   * e.g., "website-feedback-tools" → "website feedback tools"
   */
  private topicSlugToHumanReadable(slug: string): string {
    return slug.replace(/-/g, ' ').trim();
  }

  /**
   * Get info about topic differences between the two pages.
   * Returns null if topics are the same, or an object with both topics if they differ.
   */
  getTopicDifferenceInfo(): { yourTopic: string; competitorTopic: string; using: 'your' | 'competitor' } | null {
    const yourTopic = this.getMainTopic(this.yourPage);
    const competitorTopic = this.getMainTopic(this.competitorPage);
    
    if (!yourTopic || !competitorTopic) {
      return null;
    }
    
    const yourHuman = this.topicSlugToHumanReadable(yourTopic.topicSlug);
    const competitorHuman = this.topicSlugToHumanReadable(competitorTopic.topicSlug);
    
    if (yourHuman === competitorHuman) {
      return null;
    }
    
    return {
      yourTopic: yourHuman,
      competitorTopic: competitorHuman,
      using: yourTopic ? 'your' : 'competitor'
    };
  }

  /**
   * Get Topic Share coverage data for the current comparison.
   * Uses the main topic from yourPage as the topic to analyze.
   */
  getTopicShareCoverageData(): TopicShareCoverageData {
    // Default empty state
    const emptyResult: TopicShareCoverageData = {
      available: false,
      topic: '',
      yourDomain: null,
      competitorDomain: null,
      sharedEntitiesCount: 0,
      lastUpdated: null,
      reason: null,
      loading: false,
      error: null
    };

    // Check if both pages have analysis
    if (!this.hasAnalysis(this.yourPage) || !this.hasAnalysis(this.competitorPage)) {
      return emptyResult;
    }

    // Get main topics from both pages
    const yourTopic = this.getMainTopic(this.yourPage);
    const competitorTopic = this.getMainTopic(this.competitorPage);
    
    // Use yourPage's topic primarily (or competitor's as fallback)
    const mainTopic = yourTopic || competitorTopic;
    if (!mainTopic || !mainTopic.topicSlug) {
      return { ...emptyResult, reason: 'No topic signals found for either page.' };
    }

    // Convert topic slug to human-readable format for API
    // Topic Share 3 stores topics as "website feedback tools", not "website-feedback-tools"
    const humanReadableTopic = this.topicSlugToHumanReadable(mainTopic.topicSlug);

    // Get domains
    const yourDomain = this.yourPageDomain;
    const competitorDomain = this.competitorPageDomain;
    if (!yourDomain || !competitorDomain) {
      return { ...emptyResult, reason: 'Unable to determine domains.' };
    }

    // Use human-readable topic for cache key and API call
    const cacheKey = this.getTopicShareCacheKey(humanReadableTopic, yourDomain, competitorDomain);

    // Check loading state
    if (this.topicShareCoverageLoading.get(cacheKey)) {
      return { ...emptyResult, topic: humanReadableTopic, loading: true };
    }

    // Check for errors
    const error = this.topicShareCoverageError.get(cacheKey);
    if (error) {
      return { ...emptyResult, topic: humanReadableTopic, error };
    }

    // Check if data is already cached
    const cachedData = this.topicShareCoverageData.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Not cached - trigger load with human-readable topic
    this.loadTopicShareCoverage(humanReadableTopic, yourDomain, competitorDomain);
    return { ...emptyResult, topic: humanReadableTopic, loading: true };
  }

  /**
   * Toggle the expansion of top URLs in Topic Share coverage card.
   */
  toggleTopicShareUrls(): void {
    this.topicShareUrlsExpanded = !this.topicShareUrlsExpanded;
  }

  /**
   * Build URL to Topic Share 3 with pre-filled query params.
   * This enables deep linking when Topic Share data isn't available.
   * 
   * The URL includes:
   * - topic: The main topic from the comparison (human-readable format)
   * - domains: Comma-separated list of both domains being compared
   * 
   * Example: /topic-share-3?topic=website%20feedback&domains=yoursite.com,competitor.com
   */
  getTopicShareDeepLink(): string {
    const topic = this.getMainTopic(this.yourPage) || this.getMainTopic(this.competitorPage);
    const humanReadableTopic = topic ? this.topicSlugToHumanReadable(topic.topicSlug) : '';
    
    const domains: string[] = [];
    if (this.yourPageDomain) {
      domains.push(this.yourPageDomain.toLowerCase().trim());
    }
    if (this.competitorPageDomain) {
      domains.push(this.competitorPageDomain.toLowerCase().trim());
    }

    const params = new URLSearchParams();
    if (humanReadableTopic) {
      params.set('topic', humanReadableTopic);
    }
    if (domains.length > 0) {
      params.set('domains', domains.join(','));
    }

    const queryString = params.toString();
    return `/topic-share-3${queryString ? '?' + queryString : ''}`;
  }

  // Topic Signals Methods
  getTopicSignals(page: PageState): any[] {
    const signals = page.analysis?.topic?.topicSignals || [];
    // Sort by weight descending
    return [...signals].sort((a, b) => (b.weight || 0) - (a.weight || 0));
  }

  formatWeight(weight: number): string {
    return (weight * 100).toFixed(1) + '%';
  }

  isTopicSignalShared(signal: any, pageKey: 'yourPage' | 'competitorPage'): boolean {
    const currentPage = pageKey === 'yourPage' ? this.yourPage : this.competitorPage;
    const otherPage = pageKey === 'yourPage' ? this.competitorPage : this.yourPage;
    
    if (!currentPage.analysis || !otherPage.analysis) {
      return false;
    }

    const currentSignals = currentPage.analysis.topic?.topicSignals || [];
    const otherSignals = otherPage.analysis.topic?.topicSignals || [];
    
    // Check if this signal's topicSlug exists in the other page
    const currentTopicSlug = signal.topicSlug;
    return otherSignals.some((s: any) => s.topicSlug === currentTopicSlug);
  }

  // Critical Issues Methods
  getCriticalIssues(page: PageState): string[] {
    return surfacedStoredIssues(page.analysis?.criticalIssues);
  }

  toggleTopicSignals(pageKey: 'yourPage' | 'competitorPage') {
    // Sync both sides
    const newState = pageKey === 'yourPage' 
      ? !this.yourPageTopicSignalsExpanded 
      : !this.competitorPageTopicSignalsExpanded;
    this.yourPageTopicSignalsExpanded = newState;
    this.competitorPageTopicSignalsExpanded = newState;
  }

  toggleCriticalIssues(pageKey: 'yourPage' | 'competitorPage') {
    // Sync both sides
    const newState = pageKey === 'yourPage' 
      ? !this.yourPageCriticalIssuesExpanded 
      : !this.competitorPageCriticalIssuesExpanded;
    this.yourPageCriticalIssuesExpanded = newState;
    this.competitorPageCriticalIssuesExpanded = newState;
  }

  isTopicSignalsExpanded(pageKey: 'yourPage' | 'competitorPage'): boolean {
    return pageKey === 'yourPage' ? this.yourPageTopicSignalsExpanded : this.competitorPageTopicSignalsExpanded;
  }

  isCriticalIssuesExpanded(pageKey: 'yourPage' | 'competitorPage'): boolean {
    return pageKey === 'yourPage' ? this.yourPageCriticalIssuesExpanded : this.competitorPageCriticalIssuesExpanded;
  }

  // Ranked Keywords Methods
  getRankedKeywordsCount(page: PageState): number {
    return page.analysis?.rankedKeywords?.keywords?.length || 0;
  }

  getRelatedPagesCount(pageKey: 'yourPage' | 'competitorPage'): number {
    if (!this.relatedPagesData) return 0;
    if (pageKey === 'yourPage' && this.relatedPagesData.yourPage?.relatedPages) {
      return this.relatedPagesData.yourPage.relatedPages.length;
    }
    if (pageKey === 'competitorPage' && this.relatedPagesData.competitorPage?.relatedPages) {
      return this.relatedPagesData.competitorPage.relatedPages.length;
    }
    return 0;
  }

  getRankedKeywords(page: PageState): any[] {
    const keywords = page.analysis?.rankedKeywords?.keywords || [];
    // Sort by position (best first)
    return [...keywords].sort((a, b) => (a.position || 999) - (b.position || 999));
  }

  getRankedKeywordsTotalCount(page: PageState): number {
    return page.analysis?.rankedKeywords?.totalCount || 0;
  }

  toggleRankedKeywords(pageKey: 'yourPage' | 'competitorPage') {
    // Sync both sides
    const newState = pageKey === 'yourPage' 
      ? !this.yourPageRankedKeywordsExpanded 
      : !this.competitorPageRankedKeywordsExpanded;
    this.yourPageRankedKeywordsExpanded = newState;
    this.competitorPageRankedKeywordsExpanded = newState;
  }

  isRankedKeywordsExpanded(pageKey: 'yourPage' | 'competitorPage'): boolean {
    return pageKey === 'yourPage' ? this.yourPageRankedKeywordsExpanded : this.competitorPageRankedKeywordsExpanded;
  }

  toggleRelatedPages() {
    this.relatedPagesExpanded = !this.relatedPagesExpanded;
  }

  isRelatedPagesExpanded(): boolean {
    return this.relatedPagesExpanded;
  }

  isKeywordShared(pageKey: 'yourPage' | 'competitorPage', keyword: string): boolean {
    const otherPageKey = pageKey === 'yourPage' ? 'competitorPage' : 'yourPage';
    const otherPage = this[otherPageKey];
    const otherKeywords = this.getRankedKeywords(otherPage);
    // Normalize keyword for comparison (lowercase, trim)
    const normalizedKeyword = keyword.toLowerCase().trim();
    return otherKeywords.some((k: any) => k.keyword?.toLowerCase().trim() === normalizedKeyword);
  }

  // Keyword Overlap Analysis Methods
  computeKeywordOverlap(): KeywordOverlapAnalysis | null {
    if (!this.hasAnalysis(this.yourPage) || !this.hasAnalysis(this.competitorPage)) {
      return null;
    }

    const yourKeywords = this.getRankedKeywords(this.yourPage);
    const competitorKeywords = this.getRankedKeywords(this.competitorPage);

    if (yourKeywords.length === 0 && competitorKeywords.length === 0) {
      return null;
    }

    // Build map of your keywords by normalized keyword string
    const yourKeywordMap = new Map<string, any>();
    yourKeywords.forEach((k: any) => {
      const normalized = k.keyword?.toLowerCase().trim();
      if (normalized) {
        yourKeywordMap.set(normalized, k);
      }
    });

    // Build map of competitor keywords
    const competitorKeywordMap = new Map<string, any>();
    competitorKeywords.forEach((k: any) => {
      const normalized = k.keyword?.toLowerCase().trim();
      if (normalized) {
        competitorKeywordMap.set(normalized, k);
      }
    });

    const sharedKeywords: KeywordOverlapAnalysis['sharedKeywords'] = [];
    const yourUniqueKeywords: KeywordOverlapAnalysis['yourUniqueKeywords'] = [];
    const competitorUniqueKeywords: KeywordOverlapAnalysis['competitorUniqueKeywords'] = [];

    // Find shared and your-unique keywords
    yourKeywordMap.forEach((yourKw, normalized) => {
      const competitorKw = competitorKeywordMap.get(normalized);
      if (competitorKw) {
        sharedKeywords.push({
          keyword: yourKw.keyword,
          yourPosition: yourKw.position || 999,
          competitorPosition: competitorKw.position || 999,
          searchVolume: yourKw.searchVolume || competitorKw.searchVolume
        });
      } else {
        yourUniqueKeywords.push({
          keyword: yourKw.keyword,
          position: yourKw.position || 999,
          searchVolume: yourKw.searchVolume
        });
      }
    });

    // Find competitor-unique keywords
    competitorKeywordMap.forEach((competitorKw, normalized) => {
      if (!yourKeywordMap.has(normalized)) {
        competitorUniqueKeywords.push({
          keyword: competitorKw.keyword,
          position: competitorKw.position || 999,
          searchVolume: competitorKw.searchVolume
        });
      }
    });

    // Calculate average positions for shared keywords
    const sharedAvgPositionYour = sharedKeywords.length > 0
      ? sharedKeywords.reduce((sum, k) => sum + k.yourPosition, 0) / sharedKeywords.length
      : 0;
    const sharedAvgPositionCompetitor = sharedKeywords.length > 0
      ? sharedKeywords.reduce((sum, k) => sum + k.competitorPosition, 0) / sharedKeywords.length
      : 0;

    return {
      sharedKeywords,
      yourUniqueKeywords,
      competitorUniqueKeywords,
      sharedCount: sharedKeywords.length,
      yourUniqueCount: yourUniqueKeywords.length,
      competitorUniqueCount: competitorUniqueKeywords.length,
      sharedAvgPositionYour: Math.round(sharedAvgPositionYour * 10) / 10,
      sharedAvgPositionCompetitor: Math.round(sharedAvgPositionCompetitor * 10) / 10
    };
  }

  getContentOpportunities(): ContentOpportunity[] {
    const overlap = this.computeKeywordOverlap();
    if (!overlap) return [];

    // Filter competitor-unique keywords by thresholds:
    // - searchVolume >= 100 (meaningful traffic potential)
    // - position <= 20 (competitor ranks reasonably well)
    const opportunities = overlap.competitorUniqueKeywords
      .filter(k => (k.searchVolume || 0) >= 100 && k.position <= 20)
      .map(k => ({
        keyword: k.keyword,
        position: k.position,
        searchVolume: k.searchVolume || 0
      }))
      .sort((a, b) => b.searchVolume - a.searchVolume)
      .slice(0, 10); // Top 10 opportunities

    return opportunities;
  }

  toggleKeywordOpportunities() {
    this.keywordOpportunitiesExpanded = !this.keywordOpportunitiesExpanded;
  }

  // ============================================================
  // DIAGNOSIS BANNER ("Why They Probably Win")
  // ============================================================
  // 
  // This is a LIVING FEATURE that should evolve as new data becomes available.
  // The diagnosis synthesizes existing signals into 2-4 human-readable bullets
  // that read like an SEO's quick hypothesis.
  //
  // Current signals used:
  // - Keyword positions (shared keywords average position comparison)
  // - Unique keyword counts
  // - Contextual incoming links (from domain crawl)
  // - Contextual outgoing links
  // - Content freshness (last modified date)
  // - Word count / content depth
  //
  // Future enhancements (when data becomes available):
  // - Domain Topic Authority scores
  // - Topic Share coverage
  // - Backlink profile strength
  // - SERP feature coverage
  // ============================================================

  /**
   * Main entry point for computing diagnosis bullets.
   * Uses memoization to avoid recomputation on every Angular change detection cycle.
   */
  getDiagnosisBullets(): DiagnosisBullet[] {
    // Check if we have both analyses completed
    if (!this.hasAnalysis(this.yourPage) || !this.hasAnalysis(this.competitorPage)) {
      return [];
    }

    // Build cache key from relevant data that could change the diagnosis
    const cacheKey = this.buildDiagnosisCacheKey();
    
    // Return cached result if still valid
    if (this._diagnosisCache !== null && this._diagnosisCacheKey === cacheKey) {
      return this._diagnosisCache;
    }

    // Compute fresh diagnosis
    const bullets: DiagnosisBullet[] = [];

    // Collect bullets from each analyzer
    bullets.push(...this.analyzeKeywordAdvantage());
    bullets.push(...this.analyzeLinkAdvantage());
    bullets.push(...this.analyzeFreshnessAdvantage());
    bullets.push(...this.analyzeContentAdvantage());
    bullets.push(...this.analyzeTopicAuthorityAdvantage());

    // Sort by priority (lower = higher priority)
    // We show all bullets - priority is for sorting, not filtering
    // With only ~6 potential bullets, there's no need to cap the display
    bullets.sort((a, b) => a.priority - b.priority);

    // Cache the result
    this._diagnosisCache = bullets;
    this._diagnosisCacheKey = cacheKey;

    return bullets;
  }

  /**
   * Build a cache key from data that affects diagnosis computation.
   */
  private buildDiagnosisCacheKey(): string {
    const overlap = this.computeKeywordOverlap();
    const yourLinkStats = this.getLinkStats(this.yourPage);
    const competitorLinkStats = this.getLinkStats(this.competitorPage);
    const yourIncomingStats = this.getIncomingLinksStats('yourPage');
    const competitorIncomingStats = this.getIncomingLinksStats('competitorPage');
    const yourTopicFit = this.getTopicFitData('yourPage');
    const competitorTopicFit = this.getTopicFitData('competitorPage');
    
    return JSON.stringify({
      yourAnalyzedAt: this.yourPage.analysis?.analyzedAt,
      competitorAnalyzedAt: this.competitorPage.analysis?.analyzedAt,
      sharedCount: overlap?.sharedCount,
      yourUniqueCount: overlap?.yourUniqueCount,
      competitorUniqueCount: overlap?.competitorUniqueCount,
      sharedAvgYour: overlap?.sharedAvgPositionYour,
      sharedAvgCompetitor: overlap?.sharedAvgPositionCompetitor,
      yourContextual: yourLinkStats.contextual,
      competitorContextual: competitorLinkStats.contextual,
      yourIncomingContextual: yourIncomingStats.contextual,
      competitorIncomingContextual: competitorIncomingStats.contextual,
      yourWordCount: this.getWordCount(this.yourPage),
      competitorWordCount: this.getWordCount(this.competitorPage),
      // Topic Authority data
      yourTopicFitHasData: yourTopicFit.hasData,
      yourTopicFitTopicFound: yourTopicFit.topicFound,
      yourTopicFitScore: yourTopicFit.authorityScore,
      yourTopicFitTier: yourTopicFit.tier,
      competitorTopicFitHasData: competitorTopicFit.hasData,
      competitorTopicFitTopicFound: competitorTopicFit.topicFound,
      competitorTopicFitScore: competitorTopicFit.authorityScore,
      competitorTopicFitTier: competitorTopicFit.tier
    });
  }

  /**
   * Analyze keyword-related advantages.
   * Rules:
   * - Competitor avg position better by 3+ on shared keywords → priority 1
   * - Competitor has 50%+ more unique keywords → priority 2
   */
  private analyzeKeywordAdvantage(): DiagnosisBullet[] {
    const bullets: DiagnosisBullet[] = [];
    const overlap = this.computeKeywordOverlap();
    
    if (!overlap) return bullets;

    // Rule 1: Shared keyword position advantage
    // Only check if there are shared keywords
    if (overlap.sharedCount >= 3) {
      const positionDiff = overlap.sharedAvgPositionYour - overlap.sharedAvgPositionCompetitor;
      
      // Competitor ranks better by 3+ positions on average
      if (positionDiff >= 3) {
        bullets.push({
          text: `They rank better on shared keywords (avg pos ${overlap.sharedAvgPositionCompetitor.toFixed(1)} vs your ${overlap.sharedAvgPositionYour.toFixed(1)}).`,
          category: 'keywords',
          priority: 1,
          winner: 'competitor'
        });
      } 
      // You rank better by 3+ positions on average
      else if (positionDiff <= -3) {
        bullets.push({
          text: `You rank better on shared keywords (avg pos ${overlap.sharedAvgPositionYour.toFixed(1)} vs their ${overlap.sharedAvgPositionCompetitor.toFixed(1)}).`,
          category: 'keywords',
          priority: 1,
          winner: 'you'
        });
      }
    }

    // Rule 2: Unique keyword count advantage
    // Only if one side has 50%+ more unique keywords and at least 5 unique
    const yourUnique = overlap.yourUniqueCount;
    const competitorUnique = overlap.competitorUniqueCount;
    
    if (competitorUnique >= 5 && yourUnique >= 1) {
      const ratio = competitorUnique / yourUnique;
      if (ratio >= 1.5) {
        bullets.push({
          text: `They rank for more unique keywords (${competitorUnique} vs ${yourUnique}).`,
          category: 'keywords',
          priority: 2,
          winner: 'competitor'
        });
      }
    }
    
    if (yourUnique >= 5 && competitorUnique >= 1) {
      const ratio = yourUnique / competitorUnique;
      if (ratio >= 1.5) {
        bullets.push({
          text: `You rank for more unique keywords (${yourUnique} vs ${competitorUnique}).`,
          category: 'keywords',
          priority: 2,
          winner: 'you'
        });
      }
    }

    return bullets;
  }

  /**
   * Analyze link-related advantages.
   * Rules:
   * - Competitor has 50%+ more contextual incoming links → priority 1
   * - Competitor has 50%+ more contextual outgoing links → priority 3
   */
  private analyzeLinkAdvantage(): DiagnosisBullet[] {
    const bullets: DiagnosisBullet[] = [];

    // Incoming contextual links (requires domain crawl data)
    const yourIncoming = this.getIncomingLinksStats('yourPage');
    const competitorIncoming = this.getIncomingLinksStats('competitorPage');

    // Only compare if both have some incoming links data
    if (yourIncoming.contextual > 0 || competitorIncoming.contextual > 0) {
      const yourIncomingContextual = yourIncoming.contextual;
      const competitorIncomingContextual = competitorIncoming.contextual;

      // Competitor has 50%+ more contextual incoming links
      if (competitorIncomingContextual >= 3 && yourIncomingContextual >= 1) {
        const ratio = competitorIncomingContextual / yourIncomingContextual;
        if (ratio >= 1.5) {
          bullets.push({
            text: `They have more contextual incoming links (${competitorIncomingContextual} vs ${yourIncomingContextual}).`,
            category: 'links',
            priority: 1,
            winner: 'competitor'
          });
        }
      } else if (competitorIncomingContextual >= 3 && yourIncomingContextual === 0) {
        bullets.push({
          text: `They have contextual incoming links (${competitorIncomingContextual}) while you have none.`,
          category: 'links',
          priority: 1,
          winner: 'competitor'
        });
      }

      // You have 50%+ more contextual incoming links
      if (yourIncomingContextual >= 3 && competitorIncomingContextual >= 1) {
        const ratio = yourIncomingContextual / competitorIncomingContextual;
        if (ratio >= 1.5) {
          bullets.push({
            text: `You have more contextual incoming links (${yourIncomingContextual} vs ${competitorIncomingContextual}).`,
            category: 'links',
            priority: 1,
            winner: 'you'
          });
        }
      } else if (yourIncomingContextual >= 3 && competitorIncomingContextual === 0) {
        bullets.push({
          text: `You have contextual incoming links (${yourIncomingContextual}) while they have none.`,
          category: 'links',
          priority: 1,
          winner: 'you'
        });
      }
    }

    // Outgoing contextual links
    const yourOutgoing = this.getLinkStats(this.yourPage);
    const competitorOutgoing = this.getLinkStats(this.competitorPage);

    const yourOutgoingContextual = yourOutgoing.contextual;
    const competitorOutgoingContextual = competitorOutgoing.contextual;

    // Competitor has 50%+ more contextual outgoing links (and at least 5)
    if (competitorOutgoingContextual >= 5 && yourOutgoingContextual >= 1) {
      const ratio = competitorOutgoingContextual / yourOutgoingContextual;
      if (ratio >= 1.5) {
        bullets.push({
          text: `They link out to more related content (${competitorOutgoingContextual} vs ${yourOutgoingContextual} contextual links).`,
          category: 'links',
          priority: 3,
          winner: 'competitor'
        });
      }
    }

    // You have 50%+ more contextual outgoing links
    if (yourOutgoingContextual >= 5 && competitorOutgoingContextual >= 1) {
      const ratio = yourOutgoingContextual / competitorOutgoingContextual;
      if (ratio >= 1.5) {
        bullets.push({
          text: `You link out to more related content (${yourOutgoingContextual} vs ${competitorOutgoingContextual} contextual links).`,
          category: 'links',
          priority: 3,
          winner: 'you'
        });
      }
    }

    return bullets;
  }

  /**
   * Analyze freshness advantages.
   * Rules:
   * - Competitor's page is 3+ months fresher → priority 2
   */
  private analyzeFreshnessAdvantage(): DiagnosisBullet[] {
    const bullets: DiagnosisBullet[] = [];

    const yourLastModified = this.getLastModified(this.yourPage);
    const competitorLastModified = this.getLastModified(this.competitorPage);

    // Both need valid dates to compare
    if (!yourLastModified || !competitorLastModified) {
      return bullets;
    }

    const yourDate = new Date(yourLastModified);
    const competitorDate = new Date(competitorLastModified);

    // Calculate difference in months
    const diffMs = competitorDate.getTime() - yourDate.getTime();
    const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);

    // Competitor is 3+ months fresher
    if (diffMonths >= 3) {
      const monthsAgo = this.getMonthsAgo(competitorDate);
      const yourMonthsAgo = this.getMonthsAgo(yourDate);
      bullets.push({
        text: `Their content is fresher (updated ${monthsAgo} vs your ${yourMonthsAgo}).`,
        category: 'freshness',
        priority: 2,
        winner: 'competitor'
      });
    }
    // You are 3+ months fresher
    else if (diffMonths <= -3) {
      const monthsAgo = this.getMonthsAgo(yourDate);
      const competitorMonthsAgo = this.getMonthsAgo(competitorDate);
      bullets.push({
        text: `Your content is fresher (updated ${monthsAgo} vs their ${competitorMonthsAgo}).`,
        category: 'freshness',
        priority: 2,
        winner: 'you'
      });
    }

    return bullets;
  }

  /**
   * Helper to format a date as "X months ago" or "X days ago".
   */
  private getMonthsAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    }
    
    const diffMonths = Math.floor(diffDays / 30);
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  }

  /**
   * Analyze content-related advantages.
   * Rules:
   * - Competitor has 30%+ more words → priority 3
   */
  private analyzeContentAdvantage(): DiagnosisBullet[] {
    const bullets: DiagnosisBullet[] = [];

    const yourWordCount = this.getWordCount(this.yourPage) || 0;
    const competitorWordCount = this.getWordCount(this.competitorPage) || 0;

    // Only compare if both have meaningful word counts
    if (yourWordCount >= 200 && competitorWordCount >= 200) {
      // Competitor has 30%+ more words
      if (competitorWordCount > yourWordCount) {
        const ratio = competitorWordCount / yourWordCount;
        if (ratio >= 1.3) {
          bullets.push({
            text: `Their page has more comprehensive content (${competitorWordCount.toLocaleString()} vs ${yourWordCount.toLocaleString()} words).`,
            category: 'content',
            priority: 3,
            winner: 'competitor'
          });
        }
      }
      // You have 30%+ more words
      else if (yourWordCount > competitorWordCount) {
        const ratio = yourWordCount / competitorWordCount;
        if (ratio >= 1.3) {
          bullets.push({
            text: `Your page has more comprehensive content (${yourWordCount.toLocaleString()} vs ${competitorWordCount.toLocaleString()} words).`,
            category: 'content',
            priority: 3,
            winner: 'you'
          });
        }
      }
    }

    return bullets;
  }

  /**
   * Analyze topic authority advantages.
   * 
   * ## Topic Selection Logic (IMPORTANT - may need tuning)
   * 
   * Each page's "main topic" is derived from its topicSignals - the topic with the
   * highest weight. This is determined by `getTopicFitData()` which calls `getMainTopic()`.
   * 
   * The main topic comes from the page's content analysis (LLM keyphrases + keyword density),
   * NOT from any manual selection. If pages cover different topics, they'll have different
   * main topics.
   * 
   * ## Comparison Modes
   * 
   * **Same Topic Mode**: When both pages have the same main topic slug, we do a direct
   * comparison (tier vs tier, score vs score) on that shared topic.
   * 
   * **Different Topic Mode**: When pages have different main topics (common when comparing
   * pages that cover related but distinct subjects), we compare each domain's authority
   * on their respective page's main topic. This is less of a direct comparison and more
   * of a "how well does each domain cover their page's subject?" analysis.
   * 
   * ## Rules & Priorities
   * 
   * Topic authority uses priority 1.5 (between core signals at 1 and secondary signals at 2-3)
   * to ensure it surfaces in the diagnosis when available. This is an important domain-level
   * signal that provides context beyond page-level metrics.
   * 
   * **Same Topic:**
   * - Competitor has higher tier (e.g., Owned vs Aspirational) → priority 1.5
   * - Same tier but 15+ point score difference → priority 2
   * 
   * **Different Topics:**
   * - Compare tier indices; higher tier wins → priority 1.5
   * - Same tier but 15+ point score difference → priority 2
   * - Bullet text clearly indicates the topics differ
   * 
   * ## Future Considerations
   * 
   * - Could add fuzzy matching for similar topic slugs (e.g., "website-feedback" vs "website-feedback-tools")
   * - Could fetch both domains' authority on BOTH topics for a more complete comparison
   * - Could weight the comparison by how "related" the topics are
   */
  private analyzeTopicAuthorityAdvantage(): DiagnosisBullet[] {
    const bullets: DiagnosisBullet[] = [];

    const yourTopicFit = this.getTopicFitData('yourPage');
    const competitorTopicFit = this.getTopicFitData('competitorPage');

    // Only analyze if both have topic authority data with the topic found
    if (!yourTopicFit.hasData || !competitorTopicFit.hasData) {
      return bullets;
    }
    if (!yourTopicFit.topicFound || !competitorTopicFit.topicFound) {
      return bullets;
    }

    // Both need authority scores for comparison
    if (yourTopicFit.authorityScore === null || competitorTopicFit.authorityScore === null) {
      return bullets;
    }

    const yourScore = yourTopicFit.authorityScore;
    const competitorScore = competitorTopicFit.authorityScore;
    const yourTier = yourTopicFit.tier;
    const competitorTier = competitorTopicFit.tier;

    // Tier ranking for comparison (higher index = stronger tier)
    const tierRanking = ['unclassified', 'anti', 'adjacent', 'ambient', 'aspirational', 'owned'];
    const yourTierIndex = yourTier ? tierRanking.indexOf(yourTier) : -1;
    const competitorTierIndex = competitorTier ? tierRanking.indexOf(competitorTier) : -1;

    // Check if topics match (same topic = more direct comparison)
    const sameMainTopic = yourTopicFit.mainTopicSlug === competitorTopicFit.mainTopicSlug;

    if (sameMainTopic) {
      // SAME TOPIC MODE: Direct comparison on shared topic
      const topicLabel = yourTopicFit.mainTopicLabel;

      // Rule 1: Tier advantage (e.g., Owned vs Aspirational) - priority 1.5 to surface above secondary signals
      if (competitorTierIndex > yourTierIndex && competitorTierIndex >= 4) { // aspirational or owned
        bullets.push({
          text: `Their domain has stronger authority on "${topicLabel}" (${this.formatTopicTier(competitorTier)} vs ${this.formatTopicTier(yourTier)}).`,
          category: 'topicAuthority',
          priority: 1.5,
          winner: 'competitor'
        });
      } else if (yourTierIndex > competitorTierIndex && yourTierIndex >= 4) {
        bullets.push({
          text: `Your domain has stronger authority on "${topicLabel}" (${this.formatTopicTier(yourTier)} vs ${this.formatTopicTier(competitorTier)}).`,
          category: 'topicAuthority',
          priority: 1.5,
          winner: 'you'
        });
      }
      // Rule 2: Same tier but significant score difference (15+ points)
      else if (yourTierIndex === competitorTierIndex && yourTierIndex >= 3) { // at least ambient
        const scoreDiff = competitorScore - yourScore;
        if (scoreDiff >= 15) {
          bullets.push({
            text: `Their domain has higher authority on "${topicLabel}" (${competitorScore} vs ${yourScore}).`,
            category: 'topicAuthority',
            priority: 2,
            winner: 'competitor'
          });
        } else if (scoreDiff <= -15) {
          bullets.push({
            text: `Your domain has higher authority on "${topicLabel}" (${yourScore} vs ${competitorScore}).`,
            category: 'topicAuthority',
            priority: 2,
            winner: 'you'
          });
        }
      }
    } else {
      // DIFFERENT TOPIC MODE: Compare each domain's authority on their page's main topic
      // This is common when pages cover related but distinct subjects
      const yourTopicLabel = yourTopicFit.mainTopicLabel;
      const competitorTopicLabel = competitorTopicFit.mainTopicLabel;

      // Rule 1: Tier advantage - one domain is strong on their topic, other is weak - priority 1.5
      if (competitorTierIndex > yourTierIndex && competitorTierIndex >= 4) {
        bullets.push({
          text: `Their domain is ${this.formatTopicTier(competitorTier)} on "${competitorTopicLabel}" while yours is ${this.formatTopicTier(yourTier)} on "${yourTopicLabel}".`,
          category: 'topicAuthority',
          priority: 1.5,
          winner: 'competitor'
        });
      } else if (yourTierIndex > competitorTierIndex && yourTierIndex >= 4) {
        bullets.push({
          text: `Your domain is ${this.formatTopicTier(yourTier)} on "${yourTopicLabel}" while theirs is ${this.formatTopicTier(competitorTier)} on "${competitorTopicLabel}".`,
          category: 'topicAuthority',
          priority: 1.5,
          winner: 'you'
        });
      }
      // Rule 2: Same tier but significant score difference
      else if (yourTierIndex === competitorTierIndex && yourTierIndex >= 3) {
        const scoreDiff = competitorScore - yourScore;
        if (scoreDiff >= 15) {
          bullets.push({
            text: `Their domain scores higher on their topic (${competitorScore} on "${competitorTopicLabel}") vs yours (${yourScore} on "${yourTopicLabel}").`,
            category: 'topicAuthority',
            priority: 2,
            winner: 'competitor'
          });
        } else if (scoreDiff <= -15) {
          bullets.push({
            text: `Your domain scores higher on your topic (${yourScore} on "${yourTopicLabel}") vs theirs (${competitorScore} on "${competitorTopicLabel}").`,
            category: 'topicAuthority',
            priority: 2,
            winner: 'you'
          });
        }
      }
    }

    return bullets;
  }

  /**
   * Check if the diagnosis shows competitor advantages (used for UI styling).
   */
  hasCompetitorAdvantages(): boolean {
    const bullets = this.getDiagnosisBullets();
    return bullets.some(b => b.winner === 'competitor');
  }

  /**
   * Check if the diagnosis shows your advantages (used for UI styling).
   */
  hasYourAdvantages(): boolean {
    const bullets = this.getDiagnosisBullets();
    return bullets.some(b => b.winner === 'you');
  }

  isProduction = environment.production;

  autofill() {
    this.yourPageUrl = 'https://marker.io/blog/website-feedback-tools';
    this.competitorPageUrl = 'https://feedbucket.app/blog/website-feedback-tool/';
  }

  // Entity Discovery Methods

  canStartEntityDiscovery(): boolean {
    return this.hasAnalysis(this.yourPage) && this.hasAnalysis(this.competitorPage) &&
           this.getTopicSignalsCount(this.yourPage) > 0 && 
           this.getTopicSignalsCount(this.competitorPage) > 0;
  }

  extractTopicSignalsForDiscovery(): Array<{ topicSlug: string; label: string; weight: number; source: 'shared' | 'yourPage' | 'competitorPage' }> {
    if (!this.hasAnalysis(this.yourPage) || !this.hasAnalysis(this.competitorPage)) {
      return [];
    }

    const yourSignals = this.getTopicSignals(this.yourPage);
    const competitorSignals = this.getTopicSignals(this.competitorPage);

    if (yourSignals.length === 0 || competitorSignals.length === 0) {
      return [];
    }

    // Find shared topic signals (same topicSlug)
    const sharedSignals: Array<{ topicSlug: string; label: string; weight: number; source: 'shared' }> = [];
    const competitorSlugMap = new Map<string, any>();
    competitorSignals.forEach(s => {
      if (s.topicSlug) {
        competitorSlugMap.set(s.topicSlug, s);
      }
    });

    yourSignals.forEach(yourSignal => {
      if (yourSignal.topicSlug && competitorSlugMap.has(yourSignal.topicSlug)) {
        const competitorSignal = competitorSlugMap.get(yourSignal.topicSlug);
        // Use average weight for shared signals
        const avgWeight = ((yourSignal.weight || 0) + (competitorSignal.weight || 0)) / 2;
        sharedSignals.push({
          topicSlug: yourSignal.topicSlug,
          label: yourSignal.label || yourSignal.topicSlug,
          weight: avgWeight,
          source: 'shared'
        });
      }
    });

    // Sort shared signals by weight descending
    sharedSignals.sort((a, b) => b.weight - a.weight);

    // If shared signals exist, return top shared signal
    if (sharedSignals.length > 0) {
      return [sharedSignals[0]];
    }

    // Otherwise, return top from each page
    const result: Array<{ topicSlug: string; label: string; weight: number; source: 'yourPage' | 'competitorPage' }> = [];
    
    if (yourSignals.length > 0 && yourSignals[0].topicSlug) {
      result.push({
        topicSlug: yourSignals[0].topicSlug,
        label: yourSignals[0].label || yourSignals[0].topicSlug,
        weight: yourSignals[0].weight || 0,
        source: 'yourPage'
      });
    }

    if (competitorSignals.length > 0 && competitorSignals[0].topicSlug) {
      result.push({
        topicSlug: competitorSignals[0].topicSlug,
        label: competitorSignals[0].label || competitorSignals[0].topicSlug,
        weight: competitorSignals[0].weight || 0,
        source: 'competitorPage'
      });
    }

    return result;
  }

  async startEntityDiscovery() {
    if (!this.canStartEntityDiscovery()) {
      alert('Both pages must be analyzed and have topic signals before starting entity discovery.');
      return;
    }

    // Extract topic signals
    this.selectedTopicSignals = this.extractTopicSignalsForDiscovery();
    
    if (this.selectedTopicSignals.length === 0) {
      alert('No topic signals found. Please ensure both pages have topic signals.');
      return;
    }

    // Reset state
    this.resetEntityDiscoveryState();

    // Start with SERP discovery
    await this.discoverSerpResults();
  }

  resetEntityDiscoveryState() {
    this.serpResults = null;
    this.serpMetadata = null;
    this.serpCost = 0;
    this.initialEntities = [];
    this.initialEntityCostBreakdown = null;
    this.initialEntityStats = null;
    this.initialEntityMetadata = null;
    this.filteredCoreKeywords = [];
    this.filterDetails = null;
    this.filteredEntities = [];
    this.filteredEntityCostBreakdown = null;
    this.filteredEntityStats = null;
    this.sharedEntities = [];
    this.sharedEntityStats = null;
    this.sharedEntityCostBreakdown = null;
    this.sharedEntityMetadata = null;
    this.relatedPagesData = null;
    this.relatedPagesError = null;
    this.totalEntityDiscoveryRunCost = 0;
    this.totalEntityDiscoveryHistoricalCost = 0;
    this.initialEntityRunCost = 0;
    this.initialEntityHistoricalCost = 0;
    this.filteredEntityRunCost = 0;
    this.filteredEntityHistoricalCost = 0;
    this.sharedEntityHistoricalCost = 0;
    this.relatedPagesCostTotal = 0;
    this.relatedPagesHistoricalCostTotal = 0;
  }

  async discoverSerpResults() {
    if (this.selectedTopicSignals.length === 0) {
      return;
    }

    this.loadingSerp = true;
    const API_URL = environment.apiUrl + '/v1';

    try {
      // For now, use the first topic signal (we can extend to handle multiple later)
      const topicSignal = this.selectedTopicSignals[0];
      const keyword = topicSignal.topicSlug;

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/serp`,
        { keyword }
      ).toPromise();

      if (response.success) {
        this.serpResults = {
          keyword: response.data.keyword,
          results: response.data.results
        };
        this.serpMetadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          keyword: response.data.keyword,
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };

        if (this.serpMetadata?.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
          this.serpCost = response.data.tasks[0].cost;
          this.totalEntityDiscoveryRunCost += this.serpCost;
        }
      }
    } catch (error: any) {
      console.error('Error fetching SERP results:', error);
    } finally {
      this.loadingSerp = false;
    }
  }

  getAllSerpUrls(): string[] {
    if (!this.serpResults?.results) return [];
    // Limit to 10 URLs
    return this.serpResults.results.slice(0, 10).map(r => r.url);
  }

  getUniqueDomains(): string[] {
    if (!this.serpResults?.results) return [];
    const domainSet = new Set<string>();
    this.serpResults.results.forEach(result => {
      const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
      domainSet.add(domain);
    });
    return Array.from(domainSet);
  }

  async discoverInitialEntities() {
    if (!this.serpResults || this.selectedTopicSignals.length === 0) {
      throw new Error('SERP results are required. Please run SERP discovery first.');
    }

    this.loadingInitialEntities = true;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const topicSignal = this.selectedTopicSignals[0];
      const topic = topicSignal.topicSlug;
      const coreKeyword = topicSignal.topicSlug; // Use same as topic for now
      const serpPageUrls = this.getAllSerpUrls();

      if (serpPageUrls.length === 0) {
        alert('No SERP pages available for entity discovery.');
        this.loadingInitialEntities = false;
        return;
      }

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/discover-entities`,
        {
          topic,
          coreKeyword,
          serpPageUrls
        }
      ).toPromise();

      if (response.success) {
        this.initialEntities = response.data.entities || [];
        this.initialEntityCostBreakdown = response.data.costBreakdown || null;
        this.initialEntityStats = response.data.stats || null;
        this.initialEntityMetadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          topic: topic.toLowerCase().trim(),
          coreKeyword: coreKeyword.toLowerCase().trim(),
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };

        const initialEntityTotal = this.initialEntityCostBreakdown?.total || 0;
        if (initialEntityTotal > 0) {
          if (this.initialEntityMetadata?.dataSource === 'api') {
            this.initialEntityRunCost = initialEntityTotal;
            this.totalEntityDiscoveryRunCost += initialEntityTotal;
          } else if (this.initialEntityMetadata?.dataSource === 'database') {
            this.initialEntityHistoricalCost = initialEntityTotal;
            this.totalEntityDiscoveryHistoricalCost += initialEntityTotal;
          }
        }
      } else {
        alert(response.message || 'Failed to discover entities');
      }
    } catch (error: any) {
      console.error('Error discovering entities:', error);
      alert(error.error?.message || error.message || 'Failed to discover entities');
    } finally {
      this.loadingInitialEntities = false;
    }
  }

  async filterToCoreKeywords() {
    if (!this.initialEntities.length || this.selectedTopicSignals.length === 0) {
      throw new Error('Initial entities are required. Please run initial entity discovery first.');
    }

    this.loadingFilter = true;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const topicSignal = this.selectedTopicSignals[0];
      const topic = topicSignal.topicSlug;
      const coreKeyword = topicSignal.topicSlug;

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/filter-entities`,
        {
          topic,
          coreKeyword,
          entities: this.initialEntities
        }
      ).toPromise();

      if (response.success) {
        this.filteredCoreKeywords = response.data.filteredCoreKeywords || [];
        this.filterDetails = response.data.selectionDetails || null;
      } else {
        alert(response.message || 'Failed to filter entities');
      }
    } catch (error: any) {
      console.error('Error filtering entities:', error);
      alert(error.error?.message || error.message || 'Failed to filter entities');
    } finally {
      this.loadingFilter = false;
    }
  }

  async discoverFilteredEntities(forceRefresh: boolean = false) {
    if (!this.filteredCoreKeywords.length) {
      throw new Error('Core keywords are required. Please run filter step first.');
    }

    const domains = this.getUniqueDomains();
    if (domains.length === 0) {
      alert('No domains available from SERP results');
      return;
    }

    this.loadingFilteredEntities = true;
    const API_URL = environment.apiUrl + '/v1';

    try {
      console.log(`🔍 Discovering filtered entities with ${this.filteredCoreKeywords.length} core keywords (forceRefresh: ${forceRefresh})...`);
      console.log(`   Core keywords:`, this.filteredCoreKeywords);
      console.log(`   Domains:`, domains);

      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/discover-entities-filtered`,
        {
          domains,
          coreKeywords: this.filteredCoreKeywords,
          forceRefresh
        }
      ).toPromise();

      if (response.success) {
        this.filteredEntities = response.data.entities || [];
        this.filteredEntityCostBreakdown = response.data.costBreakdown || null;
        this.filteredEntityStats = response.data.stats || null;
        this.domainTotalCounts = response.metadata?.domainTotalCounts || {};

        const filteredEntityTotal = this.filteredEntityCostBreakdown?.total || 0;
        const filteredDataSource = response.metadata?.dataSource;
        if (filteredEntityTotal > 0) {
          if (filteredDataSource === 'api') {
            this.filteredEntityRunCost = filteredEntityTotal;
            this.totalEntityDiscoveryRunCost += filteredEntityTotal;
          } else if (filteredDataSource === 'database') {
            this.filteredEntityHistoricalCost = filteredEntityTotal;
            this.totalEntityDiscoveryHistoricalCost += filteredEntityTotal;
          }
        }
      } else {
        alert(response.message || 'Failed to discover filtered entities');
      }
    } catch (error: any) {
      console.error('Error discovering filtered entities:', error);
      alert(error.error?.message || error.message || 'Failed to discover filtered entities');
    } finally {
      this.loadingFilteredEntities = false;
    }
  }

  async buildSharedEntityList() {
    if (!this.initialEntities.length || this.selectedTopicSignals.length === 0) {
      throw new Error('Initial entities are required. Please complete previous steps first.');
    }
    
    // Note: filteredEntities can be empty (0 length), that's okay - we'll just use initial entities
    if (!this.filteredEntities || this.filteredEntities === undefined) {
      throw new Error('Filtered entity discovery must be completed (even if 0 results).');
    }

    const topicSignal = this.selectedTopicSignals[0];
    const topic = topicSignal.topicSlug;
    const coreKeyword = topicSignal.topicSlug;
    const serpPageUrls = this.getAllSerpUrls();
    const domains = this.getUniqueDomains();

    if (domains.length === 0) {
      alert('No domains available from SERP results');
      return;
    }

    this.loadingSharedEntities = true;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share-3/build-shared-entities`,
        {
          topic,
          coreKeyword,
          serpPageUrls,
          domains,
          coreKeywords: this.filteredCoreKeywords
        }
      ).toPromise();

      if (response.success) {
        this.sharedEntities = response.data.sharedEntities || [];
        this.sharedEntityStats = response.data.stats || null;
        this.sharedEntityCostBreakdown = response.data.costBreakdown || null;
        this.sharedEntityMetadata = response.metadata || null;

        const sharedEntityTotal = this.sharedEntityCostBreakdown?.total || 0;
        if (sharedEntityTotal > 0) {
          // This endpoint merges previously discovered data and reuses stored costs.
          // Treat it as historical/reference cost, not new spend for this run.
          this.sharedEntityHistoricalCost = sharedEntityTotal;
          this.totalEntityDiscoveryHistoricalCost += sharedEntityTotal;
        }
      } else {
        alert(response.message || 'Failed to build shared entity list');
      }
    } catch (error: any) {
      console.error('Error building shared entity list:', error);
      alert(error.error?.message || error.message || 'Failed to build shared entity list');
    } finally {
      this.loadingSharedEntities = false;
    }
  }

  async findRelatedPagesFromSharedEntities() {
    // Note: 0 shared entities is a valid result (no entities shared between domains)
    // We only check that shared entity list was built (not null/undefined)
    if (!this.sharedEntities || this.sharedEntities === undefined || this.sharedEntities === null) {
      throw new Error('Shared entity list must be built first (even if 0 results).');
    }
    
    // If there are 0 shared entities, we can still try to find related pages, but it will likely return 0 results
    // This is a valid scenario and shouldn't throw an error

    if (!this.yourPageUrl || !this.competitorPageUrl) {
      alert('Page URLs are required.');
      return;
    }

    this.loadingRelatedPages = true;
    this.relatedPagesError = null;
    this.relatedPagesMetadata = null;
    this.relatedPagesCostTotal = 0;
    this.relatedPagesHistoricalCostTotal = 0;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const sharedEntityKeywords = this.sharedEntities.map(e => e.normalizedKeyword);

      const response = await this.http.post<any>(
        `${API_URL}/urls/find-related-pages`,
        {
          yourPageUrl: this.yourPageUrl,
          competitorPageUrl: this.competitorPageUrl,
          sharedEntityKeywords
        }
      ).toPromise();

      if (response.success) {
        this.relatedPagesData = {
          yourPage: response.data.yourPage,
          competitorPage: response.data.competitorPage
        };

        // Capture metadata about how Step 6 was executed (data source, filters, costs, etc.)
        this.relatedPagesMetadata = response.metadata || null;

        // Compute total cost for Step 6 (both domains)
        const yourCost = response.data?.yourPage?.metadata?.costBreakdown?.total || 0;
        const competitorCost = response.data?.competitorPage?.metadata?.costBreakdown?.total || 0;
        const relatedPagesTotal = yourCost + competitorCost;
        if (response.metadata?.dataSource === 'database') {
          this.relatedPagesHistoricalCostTotal = relatedPagesTotal;
          this.totalEntityDiscoveryHistoricalCost += relatedPagesTotal;
        } else {
          this.relatedPagesCostTotal = relatedPagesTotal;
          this.totalEntityDiscoveryRunCost += relatedPagesTotal;
        }
      } else {
        this.relatedPagesError = response.message || 'Failed to find related pages';
      }
    } catch (error: any) {
        console.error('Error finding related pages:', error);
      this.relatedPagesError = error.error?.message || error.message || 'Failed to find related pages';
    } finally {
      this.loadingRelatedPages = false;
    }
  }

  // Helper methods for entity display
  getSimilarityColorClass(score: number): string {
    if (score >= 0.8) return 'similarity-high';
    if (score >= 0.6) return 'similarity-medium';
    if (score >= 0.4) return 'similarity-low';
    return 'similarity-very-low';
  }

  formatSimilarity(score: number): string {
    return (score * 100).toFixed(1) + '%';
  }

  getDisplayedInitialEntities(): Entity[] {
    return this.showAllInitialEntities 
      ? this.initialEntities 
      : this.initialEntities.slice(0, this.initialEntitiesDisplayLimit);
  }

  getDisplayedFilteredEntities(): Entity[] {
    return this.showAllFilteredEntities 
      ? this.filteredEntities 
      : this.filteredEntities.slice(0, this.filteredEntitiesDisplayLimit);
  }

  getDisplayedSharedEntities(): SharedEntity[] {
    return this.showAllSharedEntities 
      ? this.sharedEntities 
      : this.sharedEntities.slice(0, this.sharedEntitiesDisplayLimit);
  }

  hasMoreInitialEntities(): boolean {
    return this.initialEntities.length > this.initialEntitiesDisplayLimit;
  }

  hasMoreFilteredEntities(): boolean {
    return this.filteredEntities.length > this.filteredEntitiesDisplayLimit;
  }

  hasMoreSharedEntities(): boolean {
    return this.sharedEntities.length > this.sharedEntitiesDisplayLimit;
  }

  isRankedFromPagesArray(pages: any): boolean {
    return Array.isArray(pages) && pages.length > 0 && typeof pages[0] === 'object' && 'url' in pages[0];
  }

  getRankedFromPagesAsObjects(pages: any): Array<{ url: string; title: string; domain: string; position: number }> {
    if (this.isRankedFromPagesArray(pages)) {
      return pages as Array<{ url: string; title: string; domain: string; position: number }>;
    }
    return [];
  }

  getRankedFromPagesAsStrings(pages: any): string[] {
    if (Array.isArray(pages)) {
      if (pages.length > 0 && typeof pages[0] === 'string') {
        return pages as string[];
      }
    }
    return [];
  }

  isRankedFromPagesEmpty(pages: any): boolean {
    if (!pages) return true;
    if (Array.isArray(pages)) {
      return pages.length === 0;
    }
    return true;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  }

  // Freshness helpers
  getAnalyzedAt(page: PageState): string | null {
    if (!page.analysis?.analyzedAt) return null;
    try {
      // Return ISO string for date pipe compatibility
      const date = new Date(page.analysis.analyzedAt);
      return date.toISOString();
    } catch {
      return null;
    }
  }

  getLastModified(page: PageState): string | null {
    // Priority: Use backend-extracted date if available (most reliable)
    if (page.analysis?.lastContentModified) {
      try {
        const date = new Date(page.analysis.lastContentModified);
        return date.toISOString();
      } catch {
        // Fall through to fallback
      }
    }
    
    // Fallback: Use DataForSEO last_modified (for older analyses without extracted date)
    const lastModified = page.analysis?.rawData?.pageData?.last_modified;
    if (!lastModified) return null;
    
    // Prefer meta_tag, then sitemap, then header (as per old implementation priority)
    const timestamp = lastModified.meta_tag || lastModified.sitemap || lastModified.header;
    if (!timestamp) return null;
    
    try {
      // Parse the DataForSEO timestamp format (e.g., "2025-08-04 08:46:39 +00:00")
      // Convert to ISO format: replace space with T, replace timezone
      const isoString = timestamp.replace(' ', 'T').replace(' +00:00', 'Z').replace(' -00:00', 'Z');
      const date = new Date(isoString);
      if (isNaN(date.getTime())) {
        // Fallback to standard parsing
        return new Date(timestamp).toISOString();
      }
      return date.toISOString();
    } catch {
      return null;
    }
  }

  // User mode: Auto-run full comparison workflow
  async runFullComparison() {
    if (!this.yourPageUrl || !this.competitorPageUrl) {
      alert('Please enter both URLs');
      return;
    }

    // Explicitly leave "history view" when user starts a new run.
    this.isViewingPersistedComparison = false;

    // Reset share state for new comparison
    this.shareUrl = null;
    this.shareError = null;
    this.shareCopied = false;

    // Initialize progress tracking
    this.comparisonProgress = {
      currentStep: 'Initializing...',
      completedSteps: [],
      estimatedTimeRemaining: 0,
      stepDetails: {
        'analyze-pages': { status: 'pending' },
        'serp': { status: 'pending' },
        'initial-entities': { status: 'pending' },
        'filter': { status: 'pending' },
        'filtered-entities': { status: 'pending' },
        'shared-entities': { status: 'pending' },
        'related-pages': { status: 'pending' }
      }
    };

    try {
      // Create PageComparisonExecution record (for tracking + share links)
      try {
        const execRes = await this.http.post<{ success: boolean; executionId: string }>(
          `${environment.apiUrl}/v1/urls/page-comparison/start`,
          { yourPageUrl: this.yourPageUrl, competitorPageUrl: this.competitorPageUrl, mode: this.featureFlags.isUserView() ? 'user' : 'dev' }
        ).toPromise();
        if (execRes?.success && execRes.executionId) {
          this.executionId = execRes.executionId;
        }
      } catch {
        // Non-critical — comparison works fine without an execution record
      }
      // Step 1: Analyze both pages (if not already analyzed)
      this.comparisonProgress.currentStep = 'Analyzing pages...';
      this.comparisonProgress.stepDetails['analyze-pages'].status = 'in-progress';

      if (!this.hasAnalysis(this.yourPage) || !this.hasAnalysis(this.competitorPage)) {
        await this.comparePages();
        
        // Wait for both analyses to complete
        while (this.yourPage.status === 'checking' || this.yourPage.status === 'analyzing' ||
               this.competitorPage.status === 'checking' || this.competitorPage.status === 'analyzing') {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        if (this.yourPage.status === 'error' || this.competitorPage.status === 'error') {
          throw new Error('Page analysis failed');
        }
      }

      this.comparisonProgress.stepDetails['analyze-pages'].status = 'completed';
      this.comparisonProgress.completedSteps.push('analyze-pages');

      // Check if we can start entity discovery
      if (!this.canStartEntityDiscovery()) {
        throw new Error('Both pages must have topic signals to start entity discovery');
      }

      // Step 2: Start entity discovery workflow
      this.comparisonProgress.currentStep = 'Starting entity discovery...';
      await this.startEntityDiscovery();

      // Step 3: SERP Results
      this.comparisonProgress.currentStep = 'Getting SERP results...';
      this.comparisonProgress.stepDetails['serp'].status = 'in-progress';
      await this.discoverSerpResults();
      // Wait for loading to complete
      while (this.loadingSerp) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (!this.serpResults) {
        throw new Error('Failed to get SERP results');
      }
      this.comparisonProgress.stepDetails['serp'].status = 'completed';
      this.comparisonProgress.completedSteps.push('serp');

      // Step 4: Initial Entities
      this.comparisonProgress.currentStep = 'Discovering initial entities...';
      this.comparisonProgress.stepDetails['initial-entities'].status = 'in-progress';
      await this.discoverInitialEntities();
      // Wait for loading to complete
      while (this.loadingInitialEntities) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (!this.initialEntities || this.initialEntities.length === 0) {
        throw new Error('Failed to discover initial entities');
      }
      this.comparisonProgress.stepDetails['initial-entities'].status = 'completed';
      this.comparisonProgress.completedSteps.push('initial-entities');

      // Step 5: Filter to Core Keywords
      this.comparisonProgress.currentStep = 'Filtering to core keywords...';
      this.comparisonProgress.stepDetails['filter'].status = 'in-progress';
      await this.filterToCoreKeywords();
      // Wait for loading to complete
      while (this.loadingFilter) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (!this.filteredCoreKeywords || this.filteredCoreKeywords.length === 0) {
        throw new Error('Failed to filter to core keywords');
      }
      this.comparisonProgress.stepDetails['filter'].status = 'completed';
      this.comparisonProgress.completedSteps.push('filter');

      // Step 6: Filtered Entities
      this.comparisonProgress.currentStep = 'Discovering filtered entities...';
      this.comparisonProgress.stepDetails['filtered-entities'].status = 'in-progress';
      await this.discoverFilteredEntities();
      // Wait for loading to complete
      while (this.loadingFilteredEntities) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Note: 0 entities is a valid result (no keywords matched the filters)
      // We'll continue even if no filtered entities were found
      if (this.filteredEntities === null || this.filteredEntities === undefined) {
        throw new Error('Filtered entity discovery failed (null result)');
      }
      this.comparisonProgress.stepDetails['filtered-entities'].status = 'completed';
      this.comparisonProgress.completedSteps.push('filtered-entities');

      // Step 7: Shared Entities
      this.comparisonProgress.currentStep = 'Building shared entity list...';
      this.comparisonProgress.stepDetails['shared-entities'].status = 'in-progress';
      await this.buildSharedEntityList();
      // Wait for loading to complete
      while (this.loadingSharedEntities) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Note: 0 shared entities is a valid result (no entities shared between domains)
      // We'll continue even if no shared entities were found
      if (this.sharedEntities === null || this.sharedEntities === undefined) {
        throw new Error('Shared entity list building failed (null result)');
      }
      this.comparisonProgress.stepDetails['shared-entities'].status = 'completed';
      this.comparisonProgress.completedSteps.push('shared-entities');

      // Step 8: Related Pages
      this.comparisonProgress.currentStep = 'Finding related pages...';
      this.comparisonProgress.stepDetails['related-pages'].status = 'in-progress';
      await this.findRelatedPagesFromSharedEntities();
      // Wait for loading to complete
      while (this.loadingRelatedPages) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      this.comparisonProgress.stepDetails['related-pages'].status = 'completed';
      this.comparisonProgress.completedSteps.push('related-pages');

      this.comparisonProgress.currentStep = 'Completed!';
      this.comparisonProgress.estimatedTimeRemaining = 0;

      // Show crawl prompt after completion
      this.showCrawlPrompt = true;
      this.refreshDomainStatuses();

    } catch (error: any) {
      console.error('Full comparison error:', error);
      if (this.comparisonProgress) {
        this.comparisonProgress.currentStep = 'Error: ' + (error.message || 'Unknown error');
        // Mark current step as error
        for (const [step, details] of Object.entries(this.comparisonProgress.stepDetails)) {
          if (details.status === 'in-progress') {
            details.status = 'error';
            break;
          }
        }
      }
      alert('Comparison failed: ' + (error.message || 'Unknown error'));
    }
  }

  formatTime(seconds: number): string {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      return `${Math.round(seconds / 60)}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.round((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  get progressPercentage(): number {
    if (!this.comparisonProgress) return 0;
    const totalSteps = Object.keys(this.comparisonProgress.stepDetails).length;
    const completedSteps = this.comparisonProgress.completedSteps.length;
    return Math.round((completedSteps / totalSteps) * 100);
  }

  get progressSteps(): Array<{ name: string; completed: boolean }> {
    if (!this.comparisonProgress) return [];
    return [
      { name: 'Analyze Pages', completed: this.comparisonProgress.completedSteps.includes('analyze-pages') },
      { name: 'SERP Results', completed: this.comparisonProgress.completedSteps.includes('serp') },
      { name: 'Initial Entities', completed: this.comparisonProgress.completedSteps.includes('initial-entities') },
      { name: 'Filter Keywords', completed: this.comparisonProgress.completedSteps.includes('filter') },
      { name: 'Filtered Entities', completed: this.comparisonProgress.completedSteps.includes('filtered-entities') },
      { name: 'Shared Entities', completed: this.comparisonProgress.completedSteps.includes('shared-entities') },
      { name: 'Related Pages', completed: this.comparisonProgress.completedSteps.includes('related-pages') }
    ];
  }

  // Crawl management methods
  extractDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  get yourPageDomain(): string {
    return this.extractDomain(this.yourPageUrl);
  }

  get competitorPageDomain(): string {
    return this.extractDomain(this.competitorPageUrl);
  }

  get crawlDomains(): string[] {
    const domains = [this.yourPageDomain, this.competitorPageDomain].filter(Boolean);
    return Array.from(new Set(domains));
  }

  private ensureCrawlState(domain: string): DomainCrawlState {
    if (!this.domainCrawlStates.has(domain)) {
      this.domainCrawlStates.set(domain, {
        status: null,
        loading: false,
        starting: false,
        error: null
      });
    }
    return this.domainCrawlStates.get(domain)!;
  }

  private ensureProcessingState(domain: string): DomainProcessingState {
    if (!this.domainProcessingStates.has(domain)) {
      this.domainProcessingStates.set(domain, {
        status: null,
        loading: false,
        error: null
      });
    }
    return this.domainProcessingStates.get(domain)!;
  }

  refreshDomainStatuses(): void {
    this.crawlDomains.forEach((domain) => {
      this.loadCrawlStatus(domain);
      this.loadProcessingStatus(domain);
    });
  }

  getDomainCrawlState(domain: string): DomainCrawlState {
    return this.ensureCrawlState(domain);
  }

  getDomainProcessingState(domain: string): DomainProcessingState {
    return this.ensureProcessingState(domain);
  }

  getCrawlStatusText(status: CrawlStatus | null): string {
    if (!status || status.totalUrls === 0) return 'No crawl';
    if (status.phase === 'crawling') return 'Crawling';
    if (status.phase === 'analyzing') return 'Analyzing';
    if (status.phase === 'complete' || status.isComplete) {
      return status.pagesFailed > 0 ? 'Completed (errors)' : 'Completed';
    }
    if (status.pagesInQueue > 0) return 'In progress';
    if (status.pagesFailed > 0) return 'Completed (errors)';
    return 'Completed';
  }

  getCrawlStatusClass(status: CrawlStatus | null): string {
    if (!status || status.totalUrls === 0) return 'status-muted';
    if (status.phase === 'crawling' || status.phase === 'analyzing') return 'status-warning';
    if (status.phase === 'complete' || status.isComplete) {
      return status.pagesFailed > 0 ? 'status-error' : 'status-success';
    }
    if (status.pagesInQueue > 0) return 'status-warning';
    if (status.pagesFailed > 0) return 'status-error';
    return 'status-success';
  }

  formatProcessingStatus(status: ProcessingStatus): string {
    switch (status) {
      case 'no_data':
        return 'No data';
      case 'not_started':
        return 'Not started';
      case 'partial':
        return 'Partial';
      case 'complete':
        return 'Complete';
      default:
        return 'Unknown';
    }
  }

  getProcessingStatusClass(status: ProcessingStatus): string {
    switch (status) {
      case 'complete':
        return 'status-success';
      case 'partial':
        return 'status-warning';
      case 'not_started':
        return 'status-muted';
      case 'no_data':
        return 'status-muted';
      default:
        return 'status-muted';
    }
  }

  isAllProcessingComplete(status: DomainProcessingStatus): boolean {
    return status.linksBackfill.status === 'complete'
      && status.linksClassify.status === 'complete'
      && status.topicsMap.status === 'complete'
      && status.topicsAggregate.status === 'complete';
  }

  async runAllProcessing(domain: string) {
    this.processingRunning.set(domain, true);
    try {
      await this.crawlService.runAllProcessing(domain).toPromise();
      this.pollProcessingStatus(domain);
    } catch (error: any) {
      console.error('Failed to start processing:', error);
      alert('Failed to start processing: ' + (error.error?.message || error.message));
      this.processingRunning.set(domain, false);
    }
  }

  async runProcessingStep(domain: string, step: 'links-backfill' | 'links-classify' | 'topics-map' | 'topics-aggregate') {
    const key = domain + ':' + step;
    this.stepRunning.set(key, true);
    try {
      await this.crawlService.runProcessingStep(domain, step).toPromise();
      this.pollProcessingStatus(domain);
    } catch (error: any) {
      console.error(`Failed to start ${step}:`, error);
      alert(`Failed to start ${step}: ` + (error.error?.message || error.message));
    } finally {
      this.stepRunning.set(key, false);
    }
  }

  private pollProcessingStatus(domain: string) {
    const pollInterval = setInterval(async () => {
      try {
        const status = await this.crawlService.getProcessingStatus(domain).toPromise();
        if (status) {
          const state = this.ensureProcessingState(domain);
          state.status = status;

          if (this.isAllProcessingComplete(status)) {
            clearInterval(pollInterval);
            this.processingRunning.set(domain, false);
          }
        }
      } catch (e) {
        // Keep polling
      }
    }, 5000);

    // Safety timeout: stop polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      this.processingRunning.set(domain, false);
    }, 600000);
  }

  async startDomainCrawl(domain: string, options?: { enableBrowserRendering?: boolean }) {
    if (!domain) {
      alert('Invalid domain');
      return;
    }

    const state = this.ensureCrawlState(domain);
    state.starting = true;
    state.error = null;
    try {
      const response = await this.crawlService.startCrawl(domain, {
        limit: 750,
        priority: 'high',
        frequency: 'manual',
        enableBrowserRendering: options?.enableBrowserRendering || false
      }).toPromise();

      if (response?.success) {
        alert(`Crawl started! ${response.urlsAdded} URLs added, ${response.tasksCreated} tasks created.`);
        // Start polling for status
        this.pollCrawlStatus(domain);
      }
    } catch (error: any) {
      console.error('Failed to start crawl:', error);
      state.error = error.message || 'Failed to start crawl';
      alert('Failed to start crawl: ' + (error.message || 'Unknown error'));
    } finally {
      state.starting = false;
    }
  }

  async estimateDomainCrawl(domain: string) {
    if (!domain) return;
    this.crawlEstimateLoading.set(domain, true);
    this.crawlEstimateError.set(domain, null);
    try {
      const response = await this.crawlService.estimateCrawl(domain).toPromise();
      if (response?.success && response.data) {
        this.crawlEstimates.set(domain, response.data);
      }
    } catch (error: any) {
      const msg = error?.error?.message || error?.message || 'Failed to estimate crawl';
      this.crawlEstimateError.set(domain, msg);
    } finally {
      this.crawlEstimateLoading.set(domain, false);
    }
  }

  async confirmAndStartCrawl(domain: string) {
    this.crawlConfirmed.set(domain, true);
    await this.startDomainCrawl(domain, {
      enableBrowserRendering: this.enableCoreWebVitals.get(domain) || false
    });
  }

  loadCrawlStatus(domain: string) {
    this.updateCrawlStatus(domain, true);
  }

  pollCrawlStatus(domain: string) {
    const existingInterval = this.crawlPollingIntervals.get(domain);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Poll immediately
    this.updateCrawlStatus(domain, false);

    // Then poll every 10 seconds
    const interval = setInterval(() => {
      this.updateCrawlStatus(domain, false);
    }, 10000);
    this.crawlPollingIntervals.set(domain, interval);
  }

  async updateCrawlStatus(domain: string, showLoading: boolean) {
    const state = this.ensureCrawlState(domain);
    if (showLoading) {
      state.loading = true;
    }
    try {
      const status = await this.crawlService.getCrawlStatus(domain).toPromise();
      if (status) {
        state.status = status;
        state.error = null;

        if (!status.isActive) {
          const existingInterval = this.crawlPollingIntervals.get(domain);
          if (existingInterval) {
            clearInterval(existingInterval);
            this.crawlPollingIntervals.delete(domain);
          }
        }
      }
    } catch (error: any) {
      console.error('Failed to get crawl status:', error);
      state.error = error.message || 'Failed to get crawl status';
    } finally {
      if (showLoading) {
        state.loading = false;
      }
    }
  }

  async loadProcessingStatus(domain: string) {
    const state = this.ensureProcessingState(domain);
    state.loading = true;
    state.error = null;
    try {
      const status = await this.crawlService.getProcessingStatus(domain).toPromise();
      if (status) {
        state.status = status;
      }
    } catch (error: any) {
      console.error('Failed to get processing status:', error);
      state.error = error.message || 'Failed to get processing status';
    } finally {
      state.loading = false;
    }
  }

  // ─── Share Link ──────────────────────────────────────────────

  createShareLink() {
    if (!this.executionId || this.shareLoading) return;

    this.shareLoading = true;
    this.shareUrl = null;
    this.shareCopied = false;
    this.shareError = null;

    this.http.post<{ success: boolean; data: { token: string } }>(`${environment.apiUrl}/v1/share`, {
      resourceType: 'comparison',
      resourceId: this.executionId,
      label: `Comparison: ${this.shortenForLabel(this.yourPageUrl)} vs ${this.shortenForLabel(this.competitorPageUrl)}`,
    }).subscribe({
      next: (res) => {
        this.shareLoading = false;
        if (res.success && res.data?.token) {
          this.shareUrl = `${window.location.origin}/share/${res.data.token}`;
        } else {
          this.shareError = 'Failed to create share link';
        }
      },
      error: (err) => {
        this.shareLoading = false;
        this.shareError = err.error?.message || 'Failed to create share link';
      },
    });
  }

  copyShareUrl() {
    if (!this.shareUrl) return;
    navigator.clipboard.writeText(this.shareUrl).then(() => {
      this.shareCopied = true;
      setTimeout(() => (this.shareCopied = false), 2000);
    });
  }

  dismissShare() {
    this.shareUrl = null;
    this.shareError = null;
    this.shareCopied = false;
  }

  private shortenForLabel(url: string): string {
    try {
      const u = new URL(url);
      return u.hostname + (u.pathname.length > 20 ? u.pathname.substring(0, 20) + '…' : u.pathname);
    } catch {
      return url.length > 30 ? url.substring(0, 30) + '…' : url;
    }
  }
}

